import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { RDP_ENABLED } from '../../config/features';
import {
  Settings as SettingsIcon,
  Mic,
  Key,
  Check,
  Loader2,
  AlertCircle,
  Globe,
  Monitor,
  Wifi,
  WifiOff,
  LogIn,
  Trash2,
  Pencil,
  X,
  Sparkles,
  Save,
  PanelLeft,
  PanelRight,
  Link2,
  Star,
  Plus,
  Upload,
  Palette,
  Play,
  Bot,
  Info,
  Bell,
  Image as ImageIcon,
  Wand2,
  RefreshCw,
  FileText,
  FolderOpen,
  FolderTree,
  ListChecks,
  MessageSquare,
  MessagesSquare,
  MessageCircle,
  Users,
  Hash,
  Layers,
  ChevronRight,
  Radio,
  Bug,
  Lightbulb,
  Maximize2,
  Minimize2,
  Github,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { useSettingsStore, type VoiceEngineModel } from '../../stores/settingsStore';
import QueueSettingsPanel from './QueueSettingsPanel';
import ClaudeIdentitiesPanel from './ClaudeIdentitiesPanel';
import CopilotIdentitiesPanel from './CopilotIdentitiesPanel';
import TeamsManagementPanel from './TeamsManagementPanel';
import { useMachineStore, type MachineInfo } from '../../stores/machineStore';
import { useAuthStore } from '../../stores/authStore';
import { useProjectStore } from '../../stores/projectStore';
import { useLayoutStore } from '../../stores/layoutStore';
import { serverConnection } from '../../services/serverConnection';
import { useBackend, getBackend } from '../../services/backend';
import type { ClaudeIdentity, CopilotIdentity, WhatsAppStatus, WhatsAppBinding, TeamsStatus, TeamsBinding, SlackStatus, SlackBinding, ChannelMember, ChannelAccess, ChannelInstance, ProviderInstances, IssuesStatus, IssueWatcher, IssueRepo } from '../../services/backend/IBackend';

/** The member-permission methods shared by every channel backend (structural). */
interface ChannelMemberApi {
  listMembers(chat: string, instance?: string): Promise<{ ok: boolean; members: ChannelMember[]; error?: string }>;
  setMemberAccess(projectRoot: string, chat: string, memberId: string, access: ChannelAccess | '__clear__', instance?: string): Promise<{ ok: boolean }>;
  setDefaultAccess(projectRoot: string, chat: string, access: ChannelAccess, instance?: string): Promise<{ ok: boolean }>;
}
import QRCode from 'qrcode';
import { isCefHost } from '../../services/backend/cefUpdater';
import AIProjectDetailsModal, { type AIProjectDetails } from './AIProjectDetailsModal';
import { logger } from '@/lib/logger';
import { AGENT_CLI_CONFIG } from '@/lib/agentCliConfig';
import AgentModelPicker from '@/components/AgentModelPicker';
import { useIconPackRegistry } from '@/lib/iconPacks/registry';
import { clearAllProjectCaches, clearProjectCache } from '@/lib/projectCache';
import { checkAIServiceReady, startBulkRefreshInBackground, type RefreshFields } from '@/lib/projectAIRefresh';
import { useBulkRefreshStore } from '@/stores/bulkRefreshStore';
import {
  getProjectTerminalSettings, setProjectTerminalSettings,
  getProjectRunnerSettings, setProjectRunnerSettings,
  type RunConfiguration,
  type ShellType,
} from '@/lib/projectSettings';
import { useAvailableShells } from '@/hooks/useAvailableShells';
import { NOTIFICATION_SOUNDS, previewNotificationSound, type NotificationSoundId } from '@/lib/notificationSound';
import { detectRunConfigurations, type DetectionContext } from '@/lib/runConfigDetector';

// ─── Types ───────────────────────────────────────────────────────────────────

type SettingsSection =
  | 'app-general'
  | 'app-multi-machine'
  | 'app-hub'
  | 'app-remote-desktop'
  | 'app-voice'
  | 'app-files'
  | 'app-agents'
  | 'app-identities'
  | 'app-copilot-identities'
  | 'app-projects'
  | 'app-groups'
  | 'app-logs'
  | 'app-feedback'
  | 'app-whatsapp'
  | 'app-teams'
  | 'app-slack'
  | 'app-github'
  | 'app-accounts'
  | 'project-general'
  | 'project-appearance'
  | 'project-agent'
  | 'project-terminal'
  | 'project-queue'
  | 'project-channels';

interface NavItemDef {
  id: SettingsSection;
  icon: LucideIcon;
  label: string;
  badge?: string;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const APP_NAV: NavItemDef[] = [
  { id: 'app-general', icon: PanelLeft, label: 'General' },
  { id: 'app-multi-machine', icon: Globe, label: 'Multi-Machine', badge: 'Beta' },
  { id: 'app-hub', icon: Link2, label: 'Companion Hub' },
  // Remote Desktop settings hidden until RDP is ready to ship (see config/features)
  ...((RDP_ENABLED ? [{ id: 'app-remote-desktop', icon: Monitor, label: 'Remote Desktop', badge: 'Beta' }] : []) as NavItemDef[]),
  { id: 'app-voice', icon: Mic, label: 'Voice Input' },
  { id: 'app-files', icon: FolderTree, label: 'File Explorer' },
  { id: 'app-agents', icon: Bot, label: 'AI Agents' },
  { id: 'app-identities', icon: Key, label: 'Claude Identities' },
  { id: 'app-copilot-identities', icon: Github, label: 'Copilot Identities' },
  { id: 'app-projects', icon: Wand2, label: 'Project Maintenance' },
  { id: 'app-groups', icon: Users, label: 'Teams' },
  { id: 'app-whatsapp', icon: MessageCircle, label: 'WhatsApp' },
  { id: 'app-teams', icon: MessagesSquare, label: 'Microsoft Teams' },
  { id: 'app-slack', icon: Hash, label: 'Slack' },
  { id: 'app-github', icon: Github, label: 'GitHub' },
  { id: 'app-accounts', icon: Layers, label: 'Accounts' },
  { id: 'app-logs', icon: FileText, label: 'Logs' },
  { id: 'app-feedback', icon: MessageSquare, label: 'Feedback' },
];

const PROJECT_NAV: NavItemDef[] = [
  { id: 'project-general', icon: Info, label: 'General' },
  { id: 'project-appearance', icon: Palette, label: 'Appearance' },
  { id: 'project-agent', icon: Bot, label: 'AI Agent' },
  { id: 'project-terminal', icon: Play, label: 'Terminal & Runner' },
  { id: 'project-queue', icon: ListChecks, label: 'Ticket Queue' },
  { id: 'project-channels', icon: Radio, label: 'Channels' },
];

const EMOJIS = ['📁', '🚀', '💻', '🎮', '🎨', '📱', '🌐', '⚡', '🔧', '📊', '🎵', '📚', '🛠️', '🔬', '🎯'];

const COLORS = [
  { name: 'purple', bg: 'bg-purple-500' },
  { name: 'blue', bg: 'bg-blue-500' },
  { name: 'green', bg: 'bg-green-500' },
  { name: 'orange', bg: 'bg-orange-500' },
  { name: 'red', bg: 'bg-red-500' },
  { name: 'yellow', bg: 'bg-yellow-500' },
  { name: 'cyan', bg: 'bg-cyan-500' },
  { name: 'pink', bg: 'bg-pink-500' },
  { name: 'indigo', bg: 'bg-indigo-500' },
  { name: 'teal', bg: 'bg-teal-500' },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────

async function getSystemHostname(): Promise<string> {
  try {
    if (!isCefHost()) return 'My Machine';
    return await getBackend().system.getHostname();
  } catch {
    return 'My Machine';
  }
}

/** Resize an image file to max dimensions and return as data URL. */
function resizeImageToDataUrl(file: File, maxSize = 128): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w > maxSize || h > maxSize) {
        if (w > h) { h = Math.round((h / w) * maxSize); w = maxSize; }
        else { w = Math.round((w / h) * maxSize); h = maxSize; }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, w, h);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
    img.src = url;
  });
}

function isImageIcon(icon: string | null | undefined): boolean {
  return !!icon && icon.startsWith('data:');
}

// ─── Section Header ──────────────────────────────────────────────────────────

function SectionHeader({ title, description }: { title: string; description?: string }) {
  return (
    <div className="mb-6">
      <h3 className="text-xl font-serif italic">{title}</h3>
      {description && <p className="text-sm text-base-content/50 mt-1">{description}</p>}
    </div>
  );
}

// ─── Main Modal ──────────────────────────────────────────────────────────────

export default function SettingsModal() {
  const { settingsOpen, closeSettings } = useUIStore();
  const requestedSection = useUIStore((s) => s.requestedSettingsSection);
  const consumeRequestedSection = useUIStore((s) => s.consumeRequestedSection);
  const [section, setSection] = useState<SettingsSection>('app-general');
  const project = useProjectStore((state) => state.currentProject);
  const hasProject = !!project;

  useEffect(() => {
    if (!settingsOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') closeSettings(); };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [settingsOpen, closeSettings]);

  // Step any Tauri child webviews (BrowserTab) out of the way while
  // Settings is open — they otherwise paint on top of the modal.
  useEffect(() => {
    if (!settingsOpen) return;
    const { pushOverlay, popOverlay } = useUIStore.getState();
    pushOverlay();
    return () => popOverlay();
  }, [settingsOpen]);

  // Honor deep-link requests (e.g. bulk-refresh pill clicking back to its
  // section). Consume the request after seeking so reopening Settings later
  // lands on the last-viewed section, not the deep-linked one.
  useEffect(() => {
    if (settingsOpen && requestedSection) {
      setSection(requestedSection as SettingsSection);
      consumeRequestedSection();
    }
  }, [settingsOpen, requestedSection, consumeRequestedSection]);

  // If current section is a project section but no project exists, fall back
  useEffect(() => {
    if (!hasProject && section.startsWith('project-')) {
      setSection('app-general');
    }
  }, [hasProject, section]);

  if (!settingsOpen) return null;

  const isAppSection = section.startsWith('app-');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) closeSettings(); }}
    >
      <div className="animate-modal-enter bg-base-100 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col md:flex-row mx-4 overflow-hidden">

        {/* ── Sidebar ── */}
        <nav className="w-full md:w-56 bg-base-200/60 border-b md:border-b-0 md:border-r border-base-300 flex-shrink-0 flex flex-col">
          {/* Title */}
          <div className="px-5 pt-5 pb-2 hidden md:flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <SettingsIcon className="w-4.5 h-4.5 text-primary" />
            </div>
            <h2 className="text-base font-bold tracking-tight">Settings</h2>
          </div>

          {/* Mobile: horizontal scroll nav */}
          <div className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 scrollbar-none">
            {[...APP_NAV, ...PROJECT_NAV].map((item) => {
              const Icon = item.icon;
              const isProject = item.id.startsWith('project-');
              const disabled = isProject && !hasProject;
              return (
                <button
                  key={item.id}
                  disabled={disabled}
                  onClick={() => !disabled && setSection(item.id)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors flex-shrink-0 ${
                    section === item.id
                      ? 'bg-primary/15 text-primary'
                      : disabled
                        ? 'text-base-content/20 cursor-not-allowed'
                        : 'text-base-content/60 hover:text-base-content hover:bg-base-300/50'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {item.label}
                </button>
              );
            })}
          </div>

          {/* Desktop: vertical nav */}
          <div className="hidden md:flex flex-col flex-1 overflow-y-auto px-3 pt-3 pb-4 gap-0.5">
            <div className="text-[10px] font-bold uppercase tracking-widest text-base-content/30 px-3 mb-1">
              Application
            </div>
            {APP_NAV.map((item) => (
              <NavButton key={item.id} item={item} active={section === item.id} onClick={() => setSection(item.id)} />
            ))}

            <div className="text-[10px] font-bold uppercase tracking-widest text-base-content/30 px-3 mb-1 mt-5">
              Project
              {project && (
                <span className="ml-1.5 normal-case tracking-normal font-medium text-base-content/40">
                  — {project.name}
                </span>
              )}
            </div>
            {PROJECT_NAV.map((item) => (
              <NavButton
                key={item.id}
                item={item}
                active={section === item.id}
                disabled={!hasProject}
                onClick={() => hasProject && setSection(item.id)}
              />
            ))}
          </div>
        </nav>

        {/* ── Content ── */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0 relative">
          {/* Close button */}
          <button
            onClick={closeSettings}
            className="absolute top-3 right-3 z-10 btn btn-ghost btn-sm btn-circle"
          >
            <X className="w-4.5 h-4.5" />
          </button>

          {/* App settings content */}
          <div className={`flex-1 overflow-y-auto p-6 pr-12 ${isAppSection ? '' : 'hidden'}`}>
            <AppSettings section={section} />
          </div>

          {/* Project settings content (always mounted to preserve state) */}
          <div className={`flex-1 flex flex-col min-w-0 min-h-0 ${!isAppSection ? '' : 'hidden'}`}>
            <ProjectSettings section={section} />
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Nav Button ──────────────────────────────────────────────────────────────

function NavButton({
  item,
  active,
  disabled,
  onClick,
}: {
  item: NavItemDef;
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  const Icon = item.icon;
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors w-full text-left ${
        active
          ? 'bg-primary/10 text-primary'
          : disabled
            ? 'text-base-content/20 cursor-not-allowed'
            : 'text-base-content/60 hover:text-base-content hover:bg-base-300/50'
      }`}
    >
      <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-primary' : ''}`} />
      <span className="truncate">{item.label}</span>
      {item.badge && (
        <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ml-auto ${
          active ? 'bg-primary/20 text-primary' : 'bg-base-300 text-base-content/40'
        }`}>
          {item.badge}
        </span>
      )}
    </button>
  );
}

// ─── App Settings ────────────────────────────────────────────────────────────

function AppSettings({ section }: { section: string }) {
  const {
    whisper,
    server,
    hub,
    rdp,
    setWhisperProvider,
    setOpenAIApiKey,
    setTranscriptionMode,
    setVoiceEngineUrl,
    setVoiceEngineToken,
    setVoiceEngineModel,
    setEnhanceModelUrl,
    setEnhanceModelName,
    setEnhanceModelApiKey,
    setServerEnabled,
    setServerUrl,
    setMachineName,
    setHubAutoImport,
    setHubDefaultAgent,
    setRdpHostEnabled,
    setRdpAutoAccept,
    setRdpDefaultViewOnly,
    setRdpClipboardSync,
    isWhisperConfigured,
  } = useSettingsStore();

  const { connectionState, machines } = useMachineStore();
  const [showApiKey, setShowApiKey] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const { login: keycloakLogin } = useAuthStore();
  const [editingMachine, setEditingMachine] = useState<MachineInfo | null>(null);
  const [editingName, setEditingName] = useState('');
  const [deletingMachine, setDeletingMachine] = useState<MachineInfo | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);

  useEffect(() => {
    if (!server.machineName && isCefHost()) {
      getSystemHostname().then((hostname) => setMachineName(hostname));
    }
  }, [server.machineName, setMachineName]);

  const handleConnect = async () => {
    if (!server.url) { setServerError('Please enter a server URL'); return; }
    setIsConnecting(true);
    setServerError(null);
    try {
      const connected = await serverConnection.connect(server.url);
      if (!connected) { setServerError('Could not connect to server.'); return; }
      const authToken = useAuthStore.getState().token || server.token;
      if (authToken) {
        const result = await serverConnection.loginWithToken(authToken);
        if (result.success) {
          if (server.machineName) await serverConnection.registerMachine(server.machineName);
          const machineList = await serverConnection.listMachines();
          useMachineStore.getState().setMachines(machineList);
        }
      }
    } catch (err) {
      logger.error('SettingsModal', 'Connection error:', err);
      setServerError('Connection failed.');
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnect = () => {
    serverConnection.disconnect();
    useMachineStore.getState().setMachines([]);
    useSettingsStore.getState().setServerToken('');
  };

  const handleDeleteMachine = async (machine: MachineInfo) => {
    setIsDeleting(true);
    try {
      const success = await serverConnection.deleteMachine(machine.id);
      if (success) {
        const machineList = await serverConnection.listMachines();
        useMachineStore.getState().setMachines(machineList);
        setDeletingMachine(null);
      } else {
        setServerError('Failed to delete machine');
      }
    } catch (err) {
      logger.error('SettingsModal', 'Delete error:', err);
      setServerError('Failed to delete machine');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRenameMachine = async () => {
    if (!editingMachine || !editingName.trim()) return;
    setIsRenaming(true);
    try {
      const success = await serverConnection.renameMachine(editingMachine.id, editingName.trim());
      if (success) {
        const machineList = await serverConnection.listMachines();
        useMachineStore.getState().setMachines(machineList);
        setEditingMachine(null);
        setEditingName('');
      } else {
        setServerError('Failed to rename machine');
      }
    } catch (err) {
      logger.error('SettingsModal', 'Rename error:', err);
      setServerError('Failed to rename machine');
    } finally {
      setIsRenaming(false);
    }
  };

  const isServerConnected = connectionState === 'connected' || connectionState === 'authenticated';
  const isAuthenticated = connectionState === 'authenticated';

  return (
    <div key={section} className="animate-section-enter space-y-6">

      {/* ── General ── */}
      {section === 'app-general' && (
        <>
          <SectionHeader title="General" description="Application layout and display preferences." />
          <div className="card bg-base-200">
            <div className="card-body py-4">
              <h4 className="font-medium mb-3">Sidebar Position</h4>
              <p className="text-sm text-base-content/60 mb-4">
                Choose which side of the screen the sidebar appears on.
              </p>
              <LayoutPositionToggle />
            </div>
          </div>
        </>
      )}

      {/* ── Multi-Machine ── */}
      {section === 'app-multi-machine' && (
        <>
          <SectionHeader title="Multi-Machine Access" description="Connect to the AtelierCode server to access your projects from multiple machines." />
          <div className="card bg-base-200">
            <div className="card-body py-4">
              <div className="form-control mb-4">
                <label className="label cursor-pointer justify-start gap-4">
                  <input type="checkbox" checked={server.enabled} onChange={(e) => setServerEnabled(e.target.checked)} className="toggle toggle-primary" />
                  <div>
                    <span className="label-text font-medium">Enable Multi-Machine Access</span>
                    <p className="text-xs text-base-content/60">Connect to remote machines via WebRTC</p>
                  </div>
                </label>
              </div>

              {server.enabled && (
                <div className="space-y-4">
                  <div className="form-control">
                    <label className="label"><span className="label-text">Server URL</span></label>
                    <input type="text" value={server.url} onChange={(e) => setServerUrl(e.target.value)} placeholder="wss://ateliercode.ai" className="input input-bordered w-full" disabled={isServerConnected} />
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text">Machine Name</span></label>
                    <div className="flex gap-2 items-center">
                      <Monitor className="w-5 h-5 text-base-content/60" />
                      <input type="text" value={server.machineName} onChange={(e) => setMachineName(e.target.value)} placeholder="My Desktop PC" className="input input-bordered flex-1" />
                    </div>
                  </div>

                  <div className="bg-base-300/50 rounded-lg p-4">
                    {!isServerConnected ? (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <WifiOff className="w-4 h-4 text-base-content/60" />
                          <span className="font-medium">Not Connected</span>
                        </div>
                        <button onClick={handleConnect} disabled={isConnecting || !server.url} className="btn btn-primary btn-sm gap-2">
                          {isConnecting ? <><Loader2 className="w-4 h-4 animate-spin" />Connecting...</> : <><Wifi className="w-4 h-4" />Connect to Server</>}
                        </button>
                      </>
                    ) : !isAuthenticated ? (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <Wifi className="w-4 h-4 text-success" />
                          <span className="font-medium">Connected - Sign In Required</span>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => keycloakLogin()} className="btn btn-primary btn-sm gap-2"><LogIn className="w-4 h-4" />Sign In with AtelierCode</button>
                          <button onClick={handleDisconnect} className="btn btn-ghost btn-sm">Disconnect</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <Wifi className="w-4 h-4 text-success" />
                          <span className="font-medium text-success">Connected & Authenticated</span>
                        </div>
                        {machines.length > 0 && (
                          <div className="mb-3">
                            <p className="text-sm text-base-content/70 mb-2">Your Machines:</p>
                            <div className="space-y-2">
                              {machines.map((machine) => (
                                <div key={machine.id} className="flex items-center gap-2 bg-base-100 rounded-lg p-2">
                                  <div className={`badge ${machine.isOnline ? 'badge-success' : 'badge-ghost'} gap-1`}>
                                    <Monitor className="w-3 h-3" />
                                    {editingMachine?.id === machine.id ? (
                                      <input type="text" value={editingName} onChange={(e) => setEditingName(e.target.value)} className="input input-xs input-bordered w-32 bg-transparent" autoFocus onKeyDown={(e) => { if (e.key === 'Enter') handleRenameMachine(); if (e.key === 'Escape') { setEditingMachine(null); setEditingName(''); } }} />
                                    ) : machine.name}
                                  </div>
                                  <div className="flex-1" />
                                  {editingMachine?.id === machine.id ? (
                                    <>
                                      <button onClick={handleRenameMachine} disabled={isRenaming || !editingName.trim()} className="btn btn-xs btn-success">{isRenaming ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}</button>
                                      <button onClick={() => { setEditingMachine(null); setEditingName(''); }} className="btn btn-xs btn-ghost"><X className="w-3 h-3" /></button>
                                    </>
                                  ) : deletingMachine?.id === machine.id ? (
                                    <>
                                      <span className="text-xs text-error">Delete?</span>
                                      <button onClick={() => handleDeleteMachine(machine)} disabled={isDeleting} className="btn btn-xs btn-error">{isDeleting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Yes'}</button>
                                      <button onClick={() => setDeletingMachine(null)} className="btn btn-xs btn-ghost">No</button>
                                    </>
                                  ) : (
                                    <>
                                      <button onClick={() => { setEditingMachine(machine); setEditingName(machine.name); }} className="btn btn-xs btn-ghost" title="Rename"><Pencil className="w-3 h-3" /></button>
                                      <button onClick={() => setDeletingMachine(machine)} className="btn btn-xs btn-ghost text-error" title="Delete"><Trash2 className="w-3 h-3" /></button>
                                    </>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        <button onClick={handleDisconnect} className="btn btn-ghost btn-sm">Disconnect</button>
                      </>
                    )}
                  </div>

                  {serverError && (
                    <div className="alert alert-error"><AlertCircle className="w-5 h-5" /><span>{serverError}</span></div>
                  )}
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Companion Hub ── */}
      {section === 'app-hub' && (
        <>
          <SectionHeader title="Companion Hub" description="Configure how AtelierCode interacts with companion apps like UtterScribe via the local hub service." />
          <div className="card bg-base-200">
            <div className="card-body py-4 space-y-4">
              <div className="form-control">
                <label className="label cursor-pointer justify-start gap-4">
                  <input type="checkbox" checked={hub.autoImportEnabled} onChange={(e) => setHubAutoImport(e.target.checked)} className="toggle toggle-primary" />
                  <div>
                    <span className="label-text font-medium">Auto-Import Hub Projects</span>
                    <p className="text-xs text-base-content/60">Automatically import projects from companion apps on startup</p>
                  </div>
                </label>
              </div>
              <div className="form-control">
                <label className="label"><span className="label-text">Default Agent for Imported Projects</span></label>
                <select value={hub.defaultAgentType} onChange={(e) => setHubDefaultAgent(e.target.value)} className="select select-bordered w-full max-w-xs">
                  <option value="claude">Claude Code</option>
                  <option value="gemini">Gemini</option>
                  <option value="codex">Codex</option>
                  <option value="opencode">OpenCode</option>
                  <option value="aider">Aider</option>
                </select>
                <label className="label"><span className="label-text-alt text-base-content/60">Agent type assigned to projects imported from the hub</span></label>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── Remote Desktop ── */}
      {section === 'app-remote-desktop' && (
        <>
          <SectionHeader title="Remote Desktop" description="Allow web clients to view and control this machine's desktop via WebRTC." />
          <div className="card bg-base-200">
            <div className="card-body py-4">
              <div className="form-control mb-4">
                <label className="label cursor-pointer justify-start gap-4">
                  <input type="checkbox" checked={rdp.hostEnabled} onChange={(e) => setRdpHostEnabled(e.target.checked)} className="toggle toggle-primary" />
                  <div>
                    <span className="label-text font-medium">Enable RDP Hosting</span>
                    <p className="text-xs text-base-content/60">Allow remote clients to request screen sharing</p>
                  </div>
                </label>
              </div>
              {rdp.hostEnabled && (
                <div className="space-y-3 pl-2 border-l-2 border-primary/20 ml-6">
                  <div className="form-control">
                    <label className="label cursor-pointer justify-start gap-4">
                      <input type="checkbox" checked={rdp.autoAccept} onChange={(e) => setRdpAutoAccept(e.target.checked)} className="toggle toggle-sm" />
                      <div>
                        <span className="label-text font-medium text-sm">Auto-Accept Connections</span>
                        <p className="text-xs text-base-content/60">Skip the consent dialog for your own machines</p>
                      </div>
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer justify-start gap-4">
                      <input type="checkbox" checked={rdp.defaultViewOnly} onChange={(e) => setRdpDefaultViewOnly(e.target.checked)} className="toggle toggle-sm" />
                      <div>
                        <span className="label-text font-medium text-sm">View-Only by Default</span>
                        <p className="text-xs text-base-content/60">New connections start in view-only mode (no input)</p>
                      </div>
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label cursor-pointer justify-start gap-4">
                      <input type="checkbox" checked={rdp.clipboardSync} onChange={(e) => setRdpClipboardSync(e.target.checked)} className="toggle toggle-sm" />
                      <div>
                        <span className="label-text font-medium text-sm">Clipboard Sync</span>
                        <p className="text-xs text-base-content/60">Share clipboard between local and remote</p>
                      </div>
                    </label>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* ── Voice Input ── */}
      {section === 'app-voice' && (
        <>
          <SectionHeader title="Voice Input" description="Configure how voice recordings are transcribed to text." />
          <div className="card bg-base-200">
            <div className="card-body py-4">
              <div className="form-control mb-6">
                <label className="label"><span className="label-text font-medium">Transcription Provider</span></label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <button onClick={() => setWhisperProvider('none')} className={`btn btn-outline justify-start h-auto py-4 ${whisper.provider === 'none' ? 'btn-primary' : ''}`}>
                    <div className="text-left"><div className="font-medium">Disabled</div><div className="text-xs opacity-70">Voice input off</div></div>
                  </button>
                  <button onClick={() => setWhisperProvider('voice-engine')} className={`btn btn-outline justify-start h-auto py-4 ${whisper.provider === 'voice-engine' ? 'btn-primary' : ''}`}>
                    <div className="text-left"><div className="font-medium">Cloud (recommended)</div><div className="text-xs opacity-70">GPU cluster, no install needed</div></div>
                  </button>
                  <button onClick={() => setWhisperProvider('openai')} className={`btn btn-outline justify-start h-auto py-4 ${whisper.provider === 'openai' ? 'btn-primary' : ''}`}>
                    <div className="text-left"><div className="font-medium">OpenAI API</div><div className="text-xs opacity-70">Requires API key</div></div>
                  </button>
                </div>
              </div>

              {whisper.provider === 'openai' && (
                <div className="bg-base-300/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3"><Key className="w-4 h-4 text-primary" /><span className="font-medium">OpenAI API Configuration</span></div>
                  <div className="form-control">
                    <label className="label"><span className="label-text">API Key</span></label>
                    <div className="relative">
                      <input type={showApiKey ? 'text' : 'password'} value={whisper.openaiApiKey} onChange={(e) => setOpenAIApiKey(e.target.value)} placeholder="sk-..." className="input input-bordered w-full pr-20" />
                      <button onClick={() => setShowApiKey(!showApiKey)} className="btn btn-ghost btn-sm absolute right-2 top-1/2 -translate-y-1/2">{showApiKey ? 'Hide' : 'Show'}</button>
                    </div>
                    <label className="label"><span className="label-text-alt text-base-content/60">Stored locally, never sent to our servers.</span></label>
                  </div>
                  {whisper.openaiApiKey && (
                    <div className="flex items-center gap-2 mt-2 text-success"><Check className="w-4 h-4" /><span className="text-sm">API key configured</span></div>
                  )}
                </div>
              )}

              {whisper.provider === 'voice-engine' && (
                <div className="bg-base-300/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-2 mb-3"><Globe className="w-4 h-4 text-primary" /><span className="font-medium">Cloud Voice Engine</span></div>
                  <div className="form-control mb-3">
                    <label className="label py-1"><span className="label-text">Cluster URL</span></label>
                    <input
                      type="text"
                      value={whisper.voiceEngineUrl}
                      onChange={(e) => setVoiceEngineUrl(e.target.value)}
                      placeholder="https://transcribe.unveiledsoftwaresolutions.com"
                      className="input input-bordered w-full font-mono text-xs"
                    />
                  </div>
                  <div className="form-control mb-3">
                    <label className="label py-1"><span className="label-text">Bearer token</span></label>
                    <div className="relative">
                      <input
                        type={showApiKey ? 'text' : 'password'}
                        value={whisper.voiceEngineToken}
                        onChange={(e) => setVoiceEngineToken(e.target.value)}
                        placeholder="cluster auth token"
                        className="input input-bordered w-full pr-20 font-mono text-xs"
                      />
                      <button onClick={() => setShowApiKey(!showApiKey)} className="btn btn-ghost btn-sm absolute right-2 top-1/2 -translate-y-1/2">{showApiKey ? 'Hide' : 'Show'}</button>
                    </div>
                    <label className="label py-1">
                      <span className="label-text-alt text-base-content/60">Default works against the team's shared cluster — override only if you self-host.</span>
                    </label>
                  </div>
                  <div className="form-control mb-3">
                    <label className="label py-1"><span className="label-text">Model (overridden by profile)</span></label>
                    <select
                      value={whisper.voiceEngineModel}
                      onChange={(e) => setVoiceEngineModel(e.target.value as VoiceEngineModel)}
                      className="select select-bordered w-full"
                      disabled
                    >
                      <option value="base">base — fastest, weakest accuracy</option>
                      <option value="small">small — balanced</option>
                      <option value="medium">medium — recommended sweet spot</option>
                      <option value="large-v3">large-v3 — best accuracy, slowest</option>
                    </select>
                    <label className="label py-0">
                      <span className="label-text-alt text-base-content/60">
                        AtelierCode requests are tagged <code>profile=ateliercode</code> — the
                        cluster-side profile config picks the model (currently medium). Ask the
                        cluster operator to change it if you need a different one.
                      </span>
                    </label>
                  </div>
                  <div className="alert alert-info py-2 text-xs">
                    <span>
                      Batch mode uploads after you press stop. Live mode streams to the cluster as
                      you speak.
                    </span>
                  </div>
                </div>
              )}

              {/* Transcription Mode — only relevant when a provider is configured */}
              {whisper.provider !== 'none' && (
                <>
                  <div className="divider mt-2 mb-2"></div>
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text font-medium">Transcription Mode</span>
                    </label>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <button
                        onClick={() => setTranscriptionMode('live')}
                        className={`btn btn-outline justify-start h-auto py-3 ${whisper.transcriptionMode === 'live' ? 'btn-primary' : ''}`}
                      >
                        <div className="text-left">
                          <div className="font-medium">Live (recommended)</div>
                          <div className="text-xs opacity-70">Transcribes phrases in the background as you talk. Final paste is near-instant.</div>
                        </div>
                      </button>
                      <button
                        onClick={() => setTranscriptionMode('batch')}
                        className={`btn btn-outline justify-start h-auto py-3 ${whisper.transcriptionMode === 'batch' ? 'btn-primary' : ''}`}
                      >
                        <div className="text-left">
                          <div className="font-medium">Batch</div>
                          <div className="text-xs opacity-70">Records the whole clip, then transcribes once. Slower for long recordings but proven.</div>
                        </div>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Enhanced transcription — the local AI that cleans up the raw
                  transcript for the ✨ enhanced mic button. */}
              <div className="divider mt-2 mb-2"></div>
              <div className="form-control">
                <label className="label">
                  <span className="label-text font-medium">Enhanced transcription (AI clean-up)</span>
                </label>
                <p className="text-xs opacity-70 -mt-1 mb-2">
                  The terminal's <span className="font-medium text-violet-400">✨ enhanced mic</span> sends the raw
                  transcript to a local AI (free, on your network) which fixes mishearings using your project context
                  and returns clean, formatted text. Defaults to the local Qwen server.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <label className="form-control">
                    <span className="label-text text-xs mb-1">Model endpoint (OpenAI-compatible)</span>
                    <input
                      type="text"
                      value={whisper.enhanceModelUrl}
                      onChange={(e) => setEnhanceModelUrl(e.target.value)}
                      placeholder="http://192.168.0.21:8000/v1"
                      className="input input-bordered input-sm font-mono"
                    />
                  </label>
                  <label className="form-control">
                    <span className="label-text text-xs mb-1">Model name</span>
                    <input
                      type="text"
                      value={whisper.enhanceModelName}
                      onChange={(e) => setEnhanceModelName(e.target.value)}
                      placeholder="qwen-primary"
                      className="input input-bordered input-sm font-mono"
                    />
                  </label>
                  <label className="form-control md:col-span-2">
                    <span className="label-text text-xs mb-1">API key (Bearer token for the gateway)</span>
                    <input
                      type="password"
                      value={whisper.enhanceModelApiKey}
                      onChange={(e) => setEnhanceModelApiKey(e.target.value)}
                      placeholder="sk-local-vllm"
                      className="input input-bordered input-sm font-mono"
                    />
                  </label>
                </div>
              </div>

              <div className="divider"></div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-base-content/70">Voice Input Status</span>
                {isWhisperConfigured() ? (
                  <div className="badge badge-success gap-1"><Check className="w-3 h-3" />Ready</div>
                ) : (
                  <div className="badge badge-warning gap-1"><AlertCircle className="w-3 h-3" />Not Configured</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ── File Explorer ── */}
      {section === 'app-files' && <FileExplorerSection />}

      {/* ── AI Agents (global defaults) ── */}
      {section === 'app-agents' && <AgentDefaultsSection />}

      {/* ── Claude Identities (isolated CLAUDE_CONFIG_DIR logins) ── */}
      {section === 'app-identities' && <ClaudeIdentitiesPanel />}

      {/* ── Copilot Identities (isolated COPILOT_HOME logins) ── */}
      {section === 'app-copilot-identities' && <CopilotIdentitiesPanel />}

      {/* ── Project Maintenance ── */}
      {section === 'app-projects' && <ProjectMaintenanceSection />}

      {/* ── Teams (project groups — org grouping layer) ── */}
      {section === 'app-groups' && <TeamsManagementPanel />}

      {/* ── Logs ── */}
      {section === 'app-logs' && <LogsSection />}

      {/* ── Feedback ── */}
      {section === 'app-feedback' && <FeedbackSection />}

      {/* ── WhatsApp ── */}
      {section === 'app-whatsapp' && <WhatsAppSection />}

      {/* ── Teams ── */}
      {section === 'app-teams' && <TeamsSection />}

      {/* ── Slack ── */}
      {section === 'app-slack' && <SlackSection />}

      {/* ── GitHub (issues watcher) ── */}
      {section === 'app-github' && <GitHubSection />}

      {/* ── Accounts (multi-instance manager) ── */}
      {section === 'app-accounts' && <AccountsSection />}
    </div>
  );
}

// ─── File Explorer Section ──────────────────────────────────────────────────

function FileExplorerSection() {
  const iconPackId = useSettingsStore((s) => s.fileExplorer.iconPackId);
  const setIconPackId = useSettingsStore((s) => s.setIconPackId);
  const order = useIconPackRegistry((s) => s.order);
  const packs = useIconPackRegistry((s) => s.packs);
  const visiblePacks = order
    .map((id) => packs[id])
    .filter((p): p is NonNullable<typeof p> => Boolean(p));

  const currentProject = useProjectStore((s) => s.currentProject);
  const [clearingProject, setClearingProject] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);

  const handleClearProject = async () => {
    if (!currentProject) return;
    if (!window.confirm(`Clear cache for "${currentProject.name}"? The next project open will rebuild from scratch.`)) return;
    setClearingProject(true);
    try {
      await clearProjectCache(currentProject.id);
    } catch (e) {
      window.alert(`Failed to clear cache: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setClearingProject(false);
    }
  };

  const handleClearAll = async () => {
    if (!window.confirm('Clear cache for ALL projects? Each project will rebuild its cache the next time you open it.')) return;
    setClearingAll(true);
    try {
      const count = await clearAllProjectCaches();
      window.alert(`Cleared cache for ${count} project${count === 1 ? '' : 's'}.`);
    } catch (e) {
      window.alert(`Failed to clear caches: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setClearingAll(false);
    }
  };

  return (
    <>
      <SectionHeader
        title="File Explorer"
        description="Customize how the file tree looks. Icon packs apply immediately."
      />

      <div className="card bg-base-200">
        <div className="card-body py-4">
          <div className="form-control">
            <label className="label py-1">
              <span className="label-text font-medium">Icon Pack</span>
            </label>
            <div className="grid grid-cols-1 gap-2">
              {visiblePacks.map((pack) => (
                <label
                  key={pack.id}
                  className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                    iconPackId === pack.id
                      ? 'border-primary bg-primary/10'
                      : 'border-base-300 hover:bg-base-300/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="icon-pack"
                    className="radio radio-primary radio-sm mt-0.5"
                    checked={iconPackId === pack.id}
                    onChange={() => setIconPackId(pack.id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{pack.name}</span>
                      {pack.external && (
                        <span className="badge badge-ghost badge-sm">External</span>
                      )}
                    </div>
                    <p className="text-xs text-base-content/60 mt-0.5">{pack.description}</p>
                  </div>
                  <div className="flex gap-1">
                    {pack.getFileIcon('app.ts')}
                    {pack.getFileIcon('package.json')}
                    {pack.getFolderIcon('src', false)}
                  </div>
                </label>
              ))}
            </div>
            <label className="label py-1 mt-2">
              <span className="label-text-alt text-base-content/60">
                Drop a JSON manifest into <code>%APPDATA%/AtelierCode/icon-packs/</code> and restart
                to install your own pack.
              </span>
            </label>
          </div>
        </div>
      </div>

      <div className="card bg-base-200">
        <div className="card-body py-4">
          <h4 className="font-medium text-sm mb-1">Project caches</h4>
          <p className="text-xs text-base-content/60 mb-3">
            AtelierCode caches the file tree, search index, TypeScript workspace, and symbol index
            under <code>.ateliercode/cache/</code> (auto-gitignored) for instant project opens.
            Wipe the cache if it ever gets out of sync.
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              className="btn btn-sm btn-ghost"
              onClick={handleClearProject}
              disabled={clearingProject || !currentProject}
            >
              {clearingProject ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Clear cache for {currentProject?.name ?? 'this project'}
            </button>
            <button
              className="btn btn-sm btn-ghost"
              onClick={handleClearAll}
              disabled={clearingAll}
            >
              {clearingAll ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
              Clear all project caches
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Project Maintenance Section ────────────────────────────────────────────

// ─── AI Agents (global defaults) ─────────────────────────────────────────

/**
 * Per-agent global default-model picker. The selected model gets passed as
 * `<modelFlag> <id>` to that agent on launch, unless overridden by a
 * project-level preferredModel (set in Edit Project → AI Agent).
 *
 * Only renders an entry for agents whose AGENT_CLI_CONFIG declares a
 * `modelFlag` — Claude / Gemini / Aider currently don't, so they're
 * silently skipped here.
 */
function AgentDefaultsSection() {
  const defaultModelMap = useSettingsStore((s) => s.agent.defaultModel);
  const setAgentDefaultModel = useSettingsStore((s) => s.setAgentDefaultModel);

  const entries = Object.entries(AGENT_CLI_CONFIG)
    .filter(([, cfg]) => !!cfg.modelFlag)
    .sort(([, a], [, b]) => a.displayName.localeCompare(b.displayName));

  return (
    <>
      <SectionHeader
        title="AI Agents"
        description="Per-agent default model. The selected model becomes the default whenever you launch this agent. Individual projects can override via Edit Project → AI Agent."
      />

      {entries.length === 0 ? (
        <div className="card bg-base-200">
          <div className="card-body py-4 text-sm text-base-content/60">
            None of the configured agents expose a model-list API yet. As CLI support lands,
            their default model picker will appear here automatically.
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {entries.map(([agentType, cfg]) => (
            <div key={agentType} className="card bg-base-200">
              <div className="card-body py-4 space-y-2">
                <h4 className="font-medium text-sm">{cfg.displayName}</h4>
                <AgentModelPicker
                  agentType={agentType}
                  value={defaultModelMap[agentType]}
                  onChange={(v) => setAgentDefaultModel(agentType, v)}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Bulk "refresh project metadata with AI" UI. Lists every project in the DB
 * with a checkbox, plus per-field checkboxes (name / description / icon /
 * run configs). Runs sequentially to avoid hitting AI rate limits and to
 * make the progress meter meaningful.
 */
function ProjectMaintenanceSection() {
  const backend = useBackend();
  const projects = useProjectStore((s) => s.projects);

  // Job state lives in a Zustand store so it survives modal close — the
  // user can kick off "refresh 23 projects", close Settings, keep working,
  // and the floating pill + completion toast carry the UX.
  const status = useBulkRefreshStore((s) => s.status);
  const done = useBulkRefreshStore((s) => s.done);
  const total = useBulkRefreshStore((s) => s.total);
  const currentName = useBulkRefreshStore((s) => s.currentName);
  const succeeded = useBulkRefreshStore((s) => s.succeeded);
  const skipped = useBulkRefreshStore((s) => s.skipped);
  const failed = useBulkRefreshStore((s) => s.failed);
  const dismissBulkRefresh = useBulkRefreshStore((s) => s.dismiss);
  const running = status === 'running';

  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set(projects.map((p) => p.id)));
  const [fields, setFields] = useState<RefreshFields>({
    name: true,
    description: true,
    icon: true,
    runConfigs: true,
  });

  const toggleProject = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const selectAll = () => setSelectedIds(new Set(projects.map((p) => p.id)));
  const selectNone = () => setSelectedIds(new Set());

  const handleRun = async () => {
    if (selectedIds.size === 0) return;
    if (!fields.name && !fields.description && !fields.icon && !fields.runConfigs) return;

    // Pre-flight: only block if any AI-dependent field is selected. Run-config
    // detection is deterministic and works without an AI provider.
    if (fields.name || fields.description || fields.icon) {
      const aiError = await checkAIServiceReady(backend);
      if (aiError) {
        window.alert(aiError);
        return;
      }
    }

    const targets = projects.filter((p) => selectedIds.has(p.id));
    const confirmMsg =
      `Refresh ${targets.length} project${targets.length === 1 ? '' : 's'} with AI in the background? ` +
      'You can close this dialog and keep working — a status pill will show progress.';
    if (!window.confirm(confirmMsg)) return;

    // Fire-and-forget: the runner publishes progress/result through the
    // bulkRefreshStore + a completion toast. We intentionally do NOT await.
    startBulkRefreshInBackground(backend, targets, fields);
  };

  return (
    <>
      <SectionHeader
        title="Project Maintenance"
        description="Bulk-refresh project metadata across every project. Useful after a big repo restructure or when an icon set goes stale."
      />

      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-3">
          <div>
            <h4 className="font-medium text-sm mb-2">Refresh which fields?</h4>
            <div className="grid grid-cols-2 gap-2">
              {(['name', 'description', 'icon', 'runConfigs'] as const).map((field) => (
                <label key={field} className="label cursor-pointer justify-start gap-2 py-1">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-primary"
                    checked={fields[field] === true}
                    onChange={(e) => setFields((prev) => ({ ...prev, [field]: e.target.checked }))}
                  />
                  <span className="label-text text-sm">
                    {field === 'runConfigs' ? 'Run configurations' : field.charAt(0).toUpperCase() + field.slice(1)}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">
              Projects ({selectedIds.size} of {projects.length} selected)
            </h4>
            <div className="flex gap-2">
              <button className="btn btn-ghost btn-xs" onClick={selectAll} disabled={running}>All</button>
              <button className="btn btn-ghost btn-xs" onClick={selectNone} disabled={running}>None</button>
            </div>
          </div>
          <div className="max-h-60 overflow-y-auto border border-base-300 rounded bg-base-100">
            {projects.length === 0 ? (
              <div className="p-3 text-sm text-base-content/60">No projects yet.</div>
            ) : (
              projects.map((p) => (
                <label
                  key={p.id}
                  className="flex items-center gap-2 px-3 py-2 hover:bg-base-300/50 cursor-pointer border-b border-base-300/50 last:border-b-0"
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-primary"
                    checked={selectedIds.has(p.id)}
                    onChange={() => toggleProject(p.id)}
                    disabled={running}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{p.name}</div>
                    <div className="text-xs text-base-content/60 truncate">{p.root_path}</div>
                  </div>
                </label>
              ))
            )}
          </div>

          {running && (
            <div className="text-xs text-base-content/70">
              <Loader2 className="w-3 h-3 inline-block animate-spin mr-1" />
              Refreshing {Math.min(done + 1, total)}/{total}
              {currentName && <>: <span className="font-medium">{currentName}</span></>}
              <div className="text-[11px] text-base-content/50 mt-0.5">
                You can close this dialog — refresh will keep running.
              </div>
            </div>
          )}

          {status === 'done' && (
            <div className="text-xs text-base-content/70 flex items-center justify-between gap-2">
              <span>
                Last run — succeeded: {succeeded}, skipped: {skipped}, failed: {failed}
              </span>
              <button className="btn btn-ghost btn-xs" onClick={dismissBulkRefresh}>Dismiss</button>
            </div>
          )}

          <div className="flex justify-end">
            <button
              className="btn btn-primary btn-sm gap-2"
              onClick={handleRun}
              disabled={running || selectedIds.size === 0}
            >
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
              {running ? 'Running…' : `Refresh ${selectedIds.size} project${selectedIds.size === 1 ? '' : 's'}`}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Logs Section ───────────────────────────────────────────────────────────

function LogsSection() {
  const [entries, setEntries] = useState<{ timestamp: string; level: string; target: string; message: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [tagFilter, setTagFilter] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const backend = useBackend();

  // While the full-screen log view is open, Escape closes IT first — not the whole
  // Settings modal. The modal's Escape handler is a bubble-phase window listener, so
  // a capture-phase listener here runs first and stops it via stopImmediatePropagation.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopImmediatePropagation(); setExpanded(false); }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [expanded]);

  // Read the core's rolling JSON-Lines log via `system.read_logs` (restored after
  // the CEF/OSR migration left this stubbed). Level filtering happens core-side;
  // the tag/message filter is applied client-side below.
  const loadLogs = async () => {
    setLoading(true);
    try {
      const level = levelFilter === 'all' ? undefined : levelFilter;
      const rows = await backend.system.readLogs({ level, limit: 1000 });
      setEntries(rows);
    } catch (e) {
      logger.warn('SettingsModal', 'Failed to read application logs:', e);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadLogs(); }, [levelFilter]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(loadLogs, 5000);
    return () => clearInterval(interval);
  }, [autoRefresh, levelFilter]);

  const openLogFolder = async () => {
    // No log directory under the CEF host (see loadLogs note).
  };

  const levelColor = (level: string) => {
    switch (level.toUpperCase()) {
      case 'ERROR': return 'text-error';
      case 'WARN': return 'text-warning';
      case 'INFO': return 'text-base-content/70';
      case 'DEBUG': return 'text-base-content/50';
      default: return 'text-base-content/70';
    }
  };

  const filteredEntries = tagFilter
    ? entries.filter((e) => e.target.toLowerCase().includes(tagFilter.toLowerCase()) || e.message.toLowerCase().includes(tagFilter.toLowerCase()))
    : entries;

  // Controls row + log grid, reused inline and in the full-screen pop-out. In
  // full-screen the log box grows to fill the height (flex-1) instead of the
  // capped max-h-[400px].
  const renderPanel = (fullscreen: boolean) => (
    <>
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select
          className="select select-sm select-bordered"
          value={levelFilter}
          onChange={(e) => setLevelFilter(e.target.value)}
        >
          <option value="all">All Levels</option>
          <option value="error">Error</option>
          <option value="warn">Warn</option>
          <option value="info">Info</option>
          <option value="debug">Debug</option>
        </select>
        <input
          type="text"
          className="input input-sm input-bordered flex-1 min-w-[120px]"
          placeholder="Filter by tag or message..."
          value={tagFilter}
          onChange={(e) => setTagFilter(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-xs cursor-pointer">
          <input
            type="checkbox"
            className="checkbox checkbox-xs"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
          />
          Auto-refresh
        </label>
        <button onClick={loadLogs} className="btn btn-sm btn-ghost gap-1" title="Refresh">
          <RefreshCw className="w-3.5 h-3.5" />
        </button>
        <button onClick={openLogFolder} className="btn btn-sm btn-ghost gap-1" title="Open log folder">
          <FolderOpen className="w-3.5 h-3.5" />
        </button>
        {fullscreen ? (
          <button onClick={() => setExpanded(false)} className="btn btn-sm btn-ghost gap-1" title="Exit full screen (Esc)">
            <Minimize2 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <button onClick={() => setExpanded(true)} className="btn btn-sm btn-ghost gap-1" title="Expand to full screen">
            <Maximize2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* Log entries */}
      <div className={`bg-base-300 rounded-lg overflow-auto font-mono text-xs leading-relaxed ${fullscreen ? 'flex-1 min-h-0' : 'max-h-[400px]'}`}>
        {loading ? (
          <div className="p-4 text-center text-base-content/50">
            <Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />Loading logs...
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="p-4 text-center text-base-content/50">No log entries found</div>
        ) : (
          <table className="w-full">
            <tbody>
              {filteredEntries.map((entry, i) => (
                <tr key={i} className="border-b border-base-content/5 hover:bg-base-content/5">
                  <td className="px-2 py-1 text-base-content/50 whitespace-nowrap align-top">
                    {entry.timestamp ? new Date(entry.timestamp).toLocaleTimeString() : ''}
                  </td>
                  <td className={`px-2 py-1 font-semibold whitespace-nowrap align-top ${levelColor(entry.level)}`}>
                    {entry.level}
                  </td>
                  <td className="px-2 py-1 text-base-content/60 align-top" title={entry.target}>
                    <div className="max-w-[14rem] truncate">{entry.target}</div>
                  </td>
                  <td className="px-2 py-1 text-base-content align-top w-full whitespace-pre-wrap break-words">
                    {entry.message}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-xs text-base-content/40 mt-2">
        Showing {filteredEntries.length} entries. Logs are stored as JSON Lines and rotated daily (5 days retained).
      </div>
    </>
  );

  return (
    <>
      <SectionHeader title="Application Logs" description="View recent log entries from the desktop app and terminal service." />
      <div className="card bg-base-200">
        <div className="card-body py-4">
          {renderPanel(false)}
        </div>
      </div>

      {/* Full-screen pop-out — portalled to <body> so it overlays the whole app
          (and isn't clipped by the Settings modal's overflow / transform). */}
      {expanded && createPortal(
        <div className="fixed inset-0 z-[120] bg-base-100 flex flex-col p-4 md:p-6">
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold">Application Logs</h2>
            <button onClick={() => setExpanded(false)} className="btn btn-sm btn-ghost gap-1.5" title="Close (Esc)">
              <X className="w-4 h-4" /> Close
            </button>
          </div>
          <div className="flex flex-col flex-1 min-h-0">
            {renderPanel(true)}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}

// ─── Feedback Section ─────────────────────────────────────────────────────────

type FeedbackType = 'feature' | 'idea' | 'bug';

function toHttpBase(url: string): string {
  if (url.startsWith('wss://')) return 'https://' + url.slice(6);
  if (url.startsWith('ws://')) return 'http://' + url.slice(5);
  return url;
}

function FeedbackSection() {
  const backend = useBackend();
  const [type, setType] = useState<FeedbackType>('feature');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [attachLogs, setAttachLogs] = useState(false);
  const [logCount, setLogCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);

  // Preview how many log entries would be attached when the toggle is on.
  useEffect(() => {
    if (!attachLogs) { setLogCount(0); return; }
    let cancelled = false;
    backend.system.readLogs({ limit: 500 })
      .then((rows) => { if (!cancelled) setLogCount(rows.length); })
      .catch(() => { if (!cancelled) setLogCount(0); });
    return () => { cancelled = true; };
  }, [attachLogs, backend]);

  const submit = async () => {
    if (!title.trim()) { setStatus({ ok: false, msg: 'Please add a short title.' }); return; }
    setSubmitting(true);
    setStatus(null);
    try {
      const { useAuthStore } = await import('../../stores/authStore');
      const { useSettingsStore } = await import('../../stores/settingsStore');
      const auth = useAuthStore.getState();
      const settings = useSettingsStore.getState();
      if (!settings.server.url) {
        setStatus({ ok: false, msg: 'No AtelierCode server is configured, so feedback can’t be sent.' });
        return;
      }
      // /feedback needs a live Keycloak session — get a fresh token (refreshing if
      // near expiry). If we can't, tell the user plainly and open the login flow
      // rather than posting an unauthenticated request that 401s.
      const token = await auth.ensureFreshToken();
      if (!token) {
        setStatus({ ok: false, msg: auth.isAuthenticated
          ? 'Your session expired — please sign in again to send feedback.'
          : 'Please sign in to send feedback.' });
        auth.login();
        return;
      }
      const httpBase = toHttpBase(settings.server.url);
      const logs = attachLogs ? await backend.system.readLogs({ limit: 500 }) : [];
      const resp = await fetch(`${httpBase}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          type,
          title: title.trim(),
          description: description.trim(),
          appVersion: '',
          platform: typeof navigator !== 'undefined' ? navigator.platform : '',
          machineName: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 80) : '',
          logs,
        }),
      });
      if (resp.status === 401) {
        // Session lapsed between the token check and the request — say so + re-login.
        setStatus({ ok: false, msg: 'Your session expired — please sign in again to send feedback.' });
        auth.login();
        return;
      }
      if (!resp.ok) {
        const body = await resp.text().catch(() => '');
        throw new Error(`Server returned ${resp.status}${body ? `: ${body.slice(0, 200)}` : ''}`);
      }
      setStatus({ ok: true, msg: 'Thanks! Your feedback was sent to the AtelierCode team.' });
      setTitle(''); setDescription(''); setAttachLogs(false);
    } catch (e) {
      logger.warn('SettingsModal', 'Feedback submit failed:', e);
      setStatus({ ok: false, msg: e instanceof Error ? e.message : 'Failed to send feedback.' });
    } finally {
      setSubmitting(false);
    }
  };

  const TYPES: { id: FeedbackType; icon: LucideIcon; label: string }[] = [
    { id: 'feature', icon: Sparkles, label: 'Feature request' },
    { id: 'idea', icon: Lightbulb, label: 'Idea' },
    { id: 'bug', icon: Bug, label: 'Bug report' },
  ];

  return (
    <>
      <SectionHeader title="Send Feedback" description="Share a feature request, idea, or bug report with the AtelierCode team." />
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-4">
          {/* Type selector */}
          <div className="flex flex-wrap gap-2">
            {TYPES.map(({ id, icon: Icon, label }) => (
              <button
                key={id}
                type="button"
                onClick={() => setType(id)}
                className={`btn btn-sm gap-1.5 flex-1 min-w-[8rem] ${type === id ? 'btn-primary' : 'btn-ghost border border-base-300'}`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            ))}
          </div>

          {/* Title */}
          <div className="form-control">
            <label className="label py-1"><span className="label-text text-sm">Title</span></label>
            <input
              type="text"
              className="input input-sm input-bordered"
              maxLength={140}
              placeholder={type === 'bug' ? 'What went wrong?' : 'What would you like to see?'}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          {/* Description */}
          <div className="form-control">
            <label className="label py-1"><span className="label-text text-sm">Details</span></label>
            <textarea
              className="textarea textarea-bordered text-sm min-h-24"
              maxLength={5000}
              placeholder={type === 'bug'
                ? 'Steps to reproduce, what you expected, and what actually happened…'
                : 'Add any detail that helps us understand it…'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Attach logs */}
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={attachLogs}
              onChange={(e) => setAttachLogs(e.target.checked)}
            />
            Attach recent application logs{attachLogs && logCount > 0 ? ` (${logCount} entries)` : ''}
          </label>
          {attachLogs && (
            <p className="text-xs text-base-content/50 -mt-2">
              Recommended for bug reports — includes the most recent log lines from this session so we can debug.
            </p>
          )}

          {status && (
            <div className={`alert ${status.ok ? 'alert-success' : 'alert-error'} py-2 text-sm`}>
              {status.msg}
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="button"
              onClick={submit}
              disabled={submitting || !title.trim()}
              className="btn btn-primary btn-sm gap-1.5"
            >
              {submitting
                ? (<><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>)
                : (<><MessageSquare className="w-4 h-4" /> Send Feedback</>)}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── Accounts (multi-instance manager) ───────────────────────────────────────

const ACCOUNT_PROVIDERS: { key: 'whatsapp' | 'teams' | 'slack'; label: string; icon: LucideIcon }[] = [
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle },
  { key: 'teams', label: 'Teams', icon: Users },
  { key: 'slack', label: 'Slack', icon: Hash },
];

function AccountsSection() {
  const backend = useBackend();
  const [registry, setRegistry] = useState<Record<string, ProviderInstances>>({});
  const [statuses, setStatuses] = useState<Record<string, string>>({}); // `${provider}:${id}` -> active
  const [adding, setAdding] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    if (!backend.channels) return;
    try {
      const r = await backend.channels.listInstances();
      setRegistry(r.registry || {});
      // refresh unit status per instance
      const next: Record<string, string> = {};
      for (const prov of ACCOUNT_PROVIDERS) {
        for (const inst of (r.registry?.[prov.key]?.instances || [])) {
          try { const s = await backend.channels.instanceStatus(prov.key, inst.id); next[`${prov.key}:${inst.id}`] = s.active || '?'; } catch { /* ignore */ }
        }
      }
      setStatuses(next);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const addAccount = async (provider: string) => {
    if (!backend.channels || !newName.trim()) return;
    setBusy(true);
    try {
      const r = await backend.channels.addInstance(provider, newName.trim());
      if (r.ok && r.id) await backend.channels.connectInstance(provider, r.id);
      setNewName(''); setAdding(null);
      await load();
    } catch { /* ignore */ } finally { setBusy(false); }
  };
  const setDefault = async (provider: string, id: string) => {
    if (!backend.channels) return;
    setBusy(true);
    try { await backend.channels.setDefault(provider, id); await load(); } catch { /* ignore */ } finally { setBusy(false); }
  };
  const connect = async (provider: string, id: string) => {
    if (!backend.channels) return;
    setBusy(true);
    try { await backend.channels.connectInstance(provider, id); await load(); } catch { /* ignore */ } finally { setBusy(false); }
  };
  const remove = async (provider: string, id: string) => {
    if (!backend.channels) return;
    setBusy(true);
    try { await backend.channels.removeInstance(provider, id); await load(); } catch { /* ignore */ } finally { setBusy(false); }
  };

  if (!backend.channels) {
    return <><SectionHeader title="Accounts" description="Manage multiple accounts per channel." /><div className="text-sm text-base-content/50">Accounts are desktop-only.</div></>;
  }

  return (
    <>
      <SectionHeader title="Accounts" description="Connect multiple accounts per channel and pick a default. Each project can use the default or select a specific account under its Channels settings." />
      <div className="space-y-4">
        {ACCOUNT_PROVIDERS.map(({ key, label, icon: Icon }) => {
          const prov = registry[key] || { default: 'default', instances: [] };
          return (
            <div key={key} className="card bg-base-200">
              <div className="card-body py-4 space-y-2">
                <div className="flex items-center gap-2 font-medium text-sm"><Icon className="w-4 h-4" /> {label}</div>
                <ul className="space-y-1">
                  {prov.instances.map((inst) => {
                    const active = statuses[`${key}:${inst.id}`];
                    const isDefault = prov.default === inst.id;
                    return (
                      <li key={inst.id} className="flex items-center justify-between bg-base-300 rounded-lg px-3 py-2 text-sm gap-2">
                        <span className="flex items-center gap-2 truncate">
                          <span className="truncate">{inst.name}</span>
                          {isDefault && <span className="badge badge-primary badge-xs gap-1"><Star className="w-2.5 h-2.5" /> default</span>}
                          <span className={`badge badge-xs ${active === 'active' ? 'badge-success' : 'badge-ghost'}`}>{active === 'active' ? 'running' : (active || 'stopped')}</span>
                        </span>
                        <span className="flex items-center gap-1 shrink-0">
                          {!isDefault && <button onClick={() => setDefault(key, inst.id)} disabled={busy} className="btn btn-ghost btn-xs" title="Set as default">Set default</button>}
                          {active !== 'active' && inst.id !== 'default' && <button onClick={() => connect(key, inst.id)} disabled={busy} className="btn btn-ghost btn-xs">Connect</button>}
                          {inst.id !== 'default' && <button onClick={() => remove(key, inst.id)} disabled={busy} className="btn btn-ghost btn-xs text-error">Remove</button>}
                        </span>
                      </li>
                    );
                  })}
                </ul>
                {adding === key ? (
                  <div className="flex items-center gap-2">
                    <input autoFocus value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Account name (e.g. Unveiled)" className="input input-sm input-bordered flex-1" onKeyDown={(e) => { if (e.key === 'Enter') addAccount(key); }} />
                    <button onClick={() => addAccount(key)} disabled={busy || !newName.trim()} className="btn btn-sm btn-primary">Add</button>
                    <button onClick={() => { setAdding(null); setNewName(''); }} className="btn btn-sm btn-ghost">Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => { setAdding(key); setNewName(''); }} className="btn btn-sm btn-ghost gap-1 self-start"><Plus className="w-3.5 h-3.5" /> Add account</button>
                )}
              </div>
            </div>
          );
        })}
        <p className="text-xs text-base-content/50">After adding an account, connect it and complete sign-in in that channel's own Settings panel (WhatsApp QR / Teams device code / Slack tokens) — the new account's gateway starts automatically. Then pick it per project under Channels.</p>
      </div>
    </>
  );
}

// ─── WhatsApp (app-level: link/relink) ───────────────────────────────────────

function WhatsAppSection() {
  const backend = useBackend();
  const [status, setStatus] = useState<WhatsAppStatus | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!backend.whatsapp) { setError('The WhatsApp gateway is desktop-only.'); setLoading(false); return; }
    try {
      const s = await backend.whatsapp.status();
      setStatus(s);
      setError(s.ok === false ? (s.error || 'Gateway not reachable') : null);
      if (s.qr) {
        try { setQrDataUrl(await QRCode.toDataURL(s.qr, { width: 256, margin: 1 })); } catch { setQrDataUrl(null); }
      } else {
        setQrDataUrl(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reach the gateway. Is it running?');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);
  // Poll so the QR / link status updates live while the panel is open.
  useEffect(() => {
    const iv = setInterval(refresh, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stamp the signed-in AtelierCode user as the identity (who fromMe messages
  // belong to) once connected — so it's never a hardcoded name.
  useEffect(() => {
    (async () => {
      if (!backend.whatsapp || !status?.connected) return;
      const { useAuthStore } = await import('@/stores/authStore');
      const user = useAuthStore.getState().user;
      const name = user?.name || user?.username || user?.email;
      if (name && status.identity?.name !== name) {
        try { await backend.whatsapp.setIdentity(name, user?.id); } catch { /* non-fatal */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.connected]);

  return (
    <>
      <SectionHeader title="WhatsApp" description="Link WhatsApp once here, then bind chats to projects under each project's Channels settings." />
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-3">
          {loading ? (
            <div className="text-sm text-base-content/50"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />Checking gateway…</div>
          ) : status?.connected ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="badge badge-success badge-sm">Connected</span>
              <span>Linked as <strong>{status.user?.name || 'your WhatsApp account'}</strong></span>
            </div>
          ) : status?.qr && qrDataUrl ? (
            <div className="space-y-2">
              <p className="text-sm">Scan to link: <span className="text-base-content/60">WhatsApp → Settings → Linked devices → Link a device</span></p>
              <img src={qrDataUrl} alt="WhatsApp pairing QR" className="w-56 h-56 bg-white rounded-lg p-2" />
            </div>
          ) : (
            <div className="text-sm text-base-content/60">{error || 'Gateway not connected — waiting for a pairing code…'}</div>
          )}
          {status?.identity?.name && (
            <div className="text-xs text-base-content/50">Your own messages are attributed to <strong>{status.identity.name}</strong>.</div>
          )}
          <div className="flex gap-2">
            <button onClick={() => { setLoading(true); refresh(); }} className="btn btn-sm btn-ghost gap-1" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>
          {error && status && <div className="text-xs text-error">{error}</div>}
        </div>
      </div>
    </>
  );
}

// ─── Teams (app-level: link/relink via device code) ──────────────────────────

function TeamsSection() {
  const backend = useBackend();
  const [status, setStatus] = useState<TeamsStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [relinking, setRelinking] = useState(false);

  const refresh = async () => {
    if (!backend.teams) { setError('The Teams gateway is desktop-only.'); setLoading(false); return; }
    try {
      const s = await backend.teams.status();
      setStatus(s);
      setError(s.ok === false ? (s.error || 'Gateway not reachable') : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reach the gateway. Is it running?');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);
  // Poll so the device code / auth status updates live while the panel is open.
  useEffect(() => {
    const iv = setInterval(refresh, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stamp the signed-in AtelierCode user as the identity once authed.
  useEffect(() => {
    (async () => {
      if (!backend.teams || !status?.authed) return;
      const { useAuthStore } = await import('@/stores/authStore');
      const user = useAuthStore.getState().user;
      const name = user?.name || user?.username || user?.email;
      if (name && status.identity?.name !== name) {
        try { await backend.teams.setIdentity(name, user?.id); } catch { /* non-fatal */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.authed]);

  const doRelink = async () => {
    if (!backend.teams) return;
    setRelinking(true);
    try { await backend.teams.relink(); await refresh(); } catch { /* ignore */ }
    finally { setRelinking(false); }
  };

  const dc = status?.deviceCode;

  return (
    <>
      <SectionHeader title="Microsoft Teams" description="Link Teams once here, then bind chats or channels to projects under each project's Channels settings." />
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-3">
          {loading ? (
            <div className="text-sm text-base-content/50"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />Checking gateway…</div>
          ) : status?.authed ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="badge badge-success badge-sm">Connected</span>
              <span>Signed in as <strong>{status.user?.displayName || 'your Teams account'}</strong></span>
            </div>
          ) : dc ? (
            <div className="space-y-2">
              <p className="text-sm">To link Teams, open <a href={dc.verificationUri} target="_blank" rel="noreferrer" className="link link-primary">{dc.verificationUri}</a> and enter this code:</p>
              <div className="text-2xl font-mono font-bold tracking-widest bg-base-300 rounded-lg px-4 py-3 inline-block">{dc.userCode}</div>
              <p className="text-xs text-base-content/50">Sign in with the Microsoft account whose Teams chats you want to use.</p>
            </div>
          ) : (
            <div className="text-sm text-base-content/60">{error || 'Not connected. Click Connect to link a Teams account.'}</div>
          )}
          {status?.identity?.name && (
            <div className="text-xs text-base-content/50">Your own messages are attributed to <strong>{status.identity.name}</strong>.</div>
          )}
          <div className="flex gap-2">
            <button onClick={() => { setLoading(true); refresh(); }} className="btn btn-sm btn-ghost gap-1" title="Refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button onClick={doRelink} disabled={relinking} className="btn btn-sm btn-ghost gap-1" title="Link a different Teams account">
              {relinking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Users className="w-3.5 h-3.5" />} {status?.authed ? 'Relink' : 'Connect'}
            </button>
          </div>
          {error && status && <div className="text-xs text-error">{error}</div>}
        </div>
      </div>
    </>
  );
}

// ─── Slack (app-level: connection status) ────────────────────────────────────

function SlackSection() {
  const backend = useBackend();
  const [status, setStatus] = useState<SlackStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    if (!backend.slack) { setError('The Slack gateway is desktop-only.'); setLoading(false); return; }
    try {
      const s = await backend.slack.status();
      setStatus(s);
      setError(s.ok === false ? (s.error || 'Gateway not reachable') : null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reach the gateway. Is it running?');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);
  useEffect(() => {
    const iv = setInterval(refresh, 3000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stamp the signed-in AtelierCode user as the identity once connected.
  useEffect(() => {
    (async () => {
      if (!backend.slack || !status?.authed) return;
      const { useAuthStore } = await import('@/stores/authStore');
      const user = useAuthStore.getState().user;
      const name = user?.name || user?.username || user?.email;
      if (name && status.identity?.name !== name) {
        try { await backend.slack.setIdentity(name, user?.id); } catch { /* non-fatal */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.authed]);

  return (
    <>
      <SectionHeader title="Slack" description="Connect a Slack workspace via the slack-gateway, then bind channels to projects under each project's Channels settings." />
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-3">
          {loading ? (
            <div className="text-sm text-base-content/50"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />Checking gateway…</div>
          ) : status?.authed ? (
            <div className="flex items-center gap-2 text-sm">
              <span className="badge badge-success badge-sm">Connected</span>
              <span>Signed in as <strong>{status.user?.displayName || 'the Slack bot'}</strong></span>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm"><span className="badge badge-ghost badge-sm">Not connected</span></div>
              <p className="text-sm text-base-content/70">{error || 'The Slack bot isn’t connected yet.'}</p>
              <p className="text-xs text-base-content/50">Create a Slack app (Socket Mode), paste its bot + app tokens into <code>~/.config/unveiled/ateliercode-slack/slack.env</code>, then restart the <code>ateliercode-slack-gateway</code> service. Invite the bot to a channel with <code>/invite @AtelierCode</code>.</p>
            </div>
          )}
          {status?.identity?.name && (
            <div className="text-xs text-base-content/50">Messages this bot posts are attributed to <strong>{status.identity.name}</strong>.</div>
          )}
          <div className="flex gap-2">
            <button onClick={() => { setLoading(true); refresh(); }} className="btn btn-sm btn-ghost gap-1" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          </div>
        </div>
      </div>
    </>
  );
}

// ─── GitHub (issues watcher: watched repos + triage policy) ──────────────────

const RESOLVE_DEPTH_LABELS: Record<string, string> = {
  'draft-pr': 'Draft PR',
  'triage': 'Triage only',
  'auto-merge': 'Auto-merge',
};

function GitHubSection({ projectRoot, projectName }: { projectRoot?: string; projectName?: string } = {}) {
  const backend = useBackend();
  const scoped = !!projectRoot;
  const [status, setStatus] = useState<IssuesStatus | null>(null);
  const [pollSeconds, setPollSeconds] = useState<number | null>(null);
  const [watchers, setWatchers] = useState<IssueWatcher[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Add-watcher flow
  const [adding, setAdding] = useState(false);
  const [owner, setOwner] = useState('');
  const [owners, setOwners] = useState<string[]>([]);
  const [repos, setRepos] = useState<IssueRepo[]>([]);
  const [selectedRepo, setSelectedRepo] = useState('');
  const [labels, setLabels] = useState('');
  const [trustedAuthors, setTrustedAuthors] = useState('');
  const [resolveDepth, setResolveDepth] = useState('draft-pr');
  const [loadingOwners, setLoadingOwners] = useState(false);
  const [loadingRepos, setLoadingRepos] = useState(false);
  const [addMsg, setAddMsg] = useState<{ ok: boolean; msg: string } | null>(null);

  const refresh = async () => {
    if (!backend.issues) { setError('The GitHub issues watcher is desktop-only.'); setLoading(false); return; }
    try {
      const s = await backend.issues.status();
      setStatus(s);
      setError(null);
      try {
        const w = await backend.issues.listWatchers();
        const all = w.watchers || [];
        setWatchers(scoped ? all.filter((x) => x.projectRoot === projectRoot) : all);
        setPollSeconds(typeof w.pollSeconds === 'number' ? w.pollSeconds : null);
      } catch { /* non-fatal — status still shows */ }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to reach the watcher. Is it running?');
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectRoot]);
  useEffect(() => {
    const iv = setInterval(refresh, 4000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectRoot]);

  const toggleWatcher = async (repo: string, enabled: boolean) => {
    if (!backend.issues) return;
    setBusy(true);
    try { await backend.issues.setWatcher({ repo, enabled }); await refresh(); } catch { /* ignore */ } finally { setBusy(false); }
  };
  const doRemove = async (repo: string) => {
    if (!backend.issues) return;
    setBusy(true);
    try { await backend.issues.removeWatcher(repo); await refresh(); } catch { /* ignore */ } finally { setBusy(false); }
  };

  const pickOwner = async (o: string) => {
    setOwner(o); setRepos([]); setSelectedRepo('');
    if (!backend.issues || !o) return;
    setLoadingRepos(true);
    try {
      const d = await backend.issues.discover(o);
      setRepos(d.repos || []);
    } catch (e) {
      setAddMsg({ ok: false, msg: e instanceof Error ? e.message : 'Failed to list repos' });
    } finally {
      setLoadingRepos(false);
    }
  };

  const startAdd = async () => {
    setAdding(true); setAddMsg(null);
    setOwner(''); setOwners([]); setRepos([]); setSelectedRepo('');
    setLabels(''); setTrustedAuthors(''); setResolveDepth('draft-pr');
    if (!backend.issues) return;
    setLoadingOwners(true);
    try {
      const d = await backend.issues.discover();
      const list = [d.me, ...(d.orgs || [])].filter((o): o is string => !!o);
      setOwners(list);
      if (list.length) await pickOwner(list[0]);
    } catch (e) {
      setAddMsg({ ok: false, msg: e instanceof Error ? e.message : 'Failed to list owners' });
    } finally {
      setLoadingOwners(false);
    }
  };

  const doAdd = async () => {
    if (!backend.issues || !selectedRepo) return;
    setBusy(true); setAddMsg(null);
    try {
      const labelList = labels.split(',').map((s) => s.trim()).filter(Boolean);
      const r = await backend.issues.addWatcher({
        repo: selectedRepo,
        projectRoot,
        labels: labelList,
        requireLabel: labelList.length > 0, // empty Labels field → watch every issue
        trustedAuthors: trustedAuthors.split(',').map((s) => s.trim()).filter(Boolean),
        resolveDepth,
      });
      if (r.ok) {
        setAdding(false);
        await refresh();
        setAddMsg({ ok: true, msg: `Now watching ${selectedRepo}.` });
      } else {
        setAddMsg({ ok: false, msg: 'Could not add the watcher.' });
      }
    } catch (e) {
      setAddMsg({ ok: false, msg: e instanceof Error ? e.message : 'Add failed' });
    } finally {
      setBusy(false);
    }
  };

  const selectedRepoIsPublic = repos.find((r) => r.repo === selectedRepo)?.visibility === 'public';

  // Watcher list + add-watcher form — shared between the app panel and the per-project card.
  const watchersBlock = (
    <>
      <div>
        <div className="text-xs font-medium text-base-content/60 mb-1">Watched repositories{scoped ? ' for this project' : ''}</div>
        {watchers.length === 0 ? (
          <div className="text-sm text-base-content/50">No repositories watched yet.</div>
        ) : (
          <ul className="space-y-1">
            {watchers.map((w) => (
              <li key={w.repo} className="bg-base-300 rounded-lg text-sm">
                <div className="flex items-center justify-between gap-2 px-3 py-2">
                  <div className="flex items-center gap-1.5 min-w-0 flex-1">
                    <Github className="w-3.5 h-3.5 text-base-content/70 shrink-0" />
                    <span className="truncate font-medium">{w.repo}</span>
                    <span className={`badge badge-xs ${w.visibility === 'public' ? 'badge-ghost' : 'badge-warning'}`}>{w.visibility}</span>
                    <span className="badge badge-xs badge-info">{RESOLVE_DEPTH_LABELS[w.resolveDepth] || w.resolveDepth}</span>
                    <span className="text-xs text-base-content/40 truncate">
                      {w.requireLabel && w.labels.length > 0 ? w.labels.map((l) => `#${l}`).join(' ') : 'all issues'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <input
                      type="checkbox"
                      className="toggle toggle-sm toggle-success"
                      checked={w.enabled}
                      disabled={busy}
                      onChange={(e) => toggleWatcher(w.repo, e.target.checked)}
                      title={w.enabled ? 'Enabled' : 'Disabled'}
                    />
                    <button onClick={() => doRemove(w.repo)} disabled={busy} className="btn btn-ghost btn-xs text-error">Remove</button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Add a watcher */}
      {!adding ? (
        <button onClick={startAdd} disabled={busy || !backend.issues} className="btn btn-sm btn-primary gap-1 self-start"><Plus className="w-3.5 h-3.5" /> Watch a repository</button>
      ) : (
        <div className="space-y-3 border-t border-base-content/10 pt-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-base-content/60">Watch a repository</span>
            <button onClick={() => { setAdding(false); setAddMsg(null); }} className="btn btn-ghost btn-xs"><X className="w-3 h-3" /></button>
          </div>

          {/* Owner */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-base-content/60 w-16">Owner</span>
            {loadingOwners ? (
              <span className="text-sm text-base-content/50"><Loader2 className="w-3.5 h-3.5 animate-spin inline-block mr-1" />Loading owners…</span>
            ) : (
              <select className="select select-sm select-bordered flex-1 min-w-[12rem]" value={owner} onChange={(e) => pickOwner(e.target.value)} disabled={owners.length === 0}>
                {owners.length === 0 && <option value="">No owners available</option>}
                {owners.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            )}
          </div>

          {/* Repo */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-base-content/60 w-16">Repository</span>
            {loadingRepos ? (
              <span className="text-sm text-base-content/50"><Loader2 className="w-3.5 h-3.5 animate-spin inline-block mr-1" />Loading repos…</span>
            ) : (
              <select className="select select-sm select-bordered flex-1 min-w-[12rem]" value={selectedRepo} onChange={(e) => setSelectedRepo(e.target.value)} disabled={repos.length === 0}>
                <option value="">{repos.length ? 'Select a repository…' : 'No repositories found'}</option>
                {repos.map((r) => (
                  <option key={r.repo} value={r.repo} disabled={r.watched}>
                    {r.repo} ({r.visibility}){r.watched ? ' — already watched' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Labels */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-base-content/60 w-16">Labels</span>
            <input className="input input-sm input-bordered flex-1 min-w-[12rem]" value={labels} onChange={(e) => setLabels(e.target.value)} placeholder="leave empty to watch every issue" />
            <p className="text-xs text-base-content/40 w-full">
              {labels.trim()
                ? `Only issues carrying one of these labels are triaged (${labels.split(',').map((s) => s.trim()).filter(Boolean).map((l) => `#${l}`).join(' ')}).`
                : 'Empty → every new issue is triaged, regardless of label (still gated by the body-length and trust checks).'}
            </p>
          </div>

          {/* Resolve depth */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-base-content/60 w-16">Policy</span>
            <select className="select select-sm select-bordered flex-1 min-w-[12rem]" value={resolveDepth} onChange={(e) => setResolveDepth(e.target.value)}>
              <option value="draft-pr">Draft PR — open a draft PR with a fix</option>
              <option value="triage">Triage only — label &amp; comment, no code</option>
              <option value="auto-merge">Auto-merge — fix and merge when green</option>
            </select>
          </div>

          {/* Trusted authors — relevant for public repos */}
          {selectedRepoIsPublic && (
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-base-content/60 w-16">Trusted</span>
              <input className="input input-sm input-bordered flex-1 min-w-[12rem]" value={trustedAuthors} onChange={(e) => setTrustedAuthors(e.target.value)} placeholder="octocat, maintainer2" />
              <p className="text-xs text-base-content/40 w-full">Comma-separated GitHub logins whose issues are acted on automatically. On public repos only trusted authors are handled.</p>
            </div>
          )}

          <div className="flex gap-2">
            <button onClick={doAdd} disabled={!selectedRepo || busy} className="btn btn-sm btn-primary gap-1"><Plus className="w-3.5 h-3.5" /> Add watcher</button>
            <button onClick={() => { setAdding(false); setAddMsg(null); }} className="btn btn-sm btn-ghost">Cancel</button>
          </div>
        </div>
      )}

      {addMsg && <div className={`alert ${addMsg.ok ? 'alert-success' : 'alert-error'} py-2 text-sm`}>{addMsg.msg}</div>}
    </>
  );

  // Project-scoped render: a single GitHub card inside the project's Channels section.
  if (scoped) {
    if (!backend.issues) return null;
    return (
      <div className="card bg-base-200 mt-4">
        <div className="card-body py-4 space-y-4">
          <div className="flex items-start gap-2 text-sm flex-wrap">
            <Github className="w-4 h-4 mt-0.5 shrink-0" />
            <div className="min-w-0">
              <span className="font-medium">GitHub issues</span>
              {status?.connected
                ? <span className="text-xs text-base-content/50"> — new issues on these repos are filed into <strong>{projectName}</strong>’s work queue and resolved as draft PRs. Connected as {status.login}.</span>
                : <span className="text-xs text-warning"> — not connected. Run <code>gh auth login</code> in a terminal, then Refresh.</span>}
            </div>
          </div>
          {watchersBlock}
        </div>
      </div>
    );
  }

  // App-level render: connection status only; per-repo management lives in each project's Channels.
  return (
    <>
      <SectionHeader title="GitHub" description="Connect GitHub so AtelierCode can watch repositories for new issues and triage them automatically. Auth is handled by the gh CLI. Choose which repos feed a project under that project’s Channels settings." />

      {/* Connection + daemon status */}
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-3">
          {loading ? (
            <div className="text-sm text-base-content/50"><Loader2 className="w-4 h-4 animate-spin inline-block mr-2" />Checking watcher…</div>
          ) : status?.connected ? (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm flex-wrap">
                <span className="badge badge-success badge-sm">Connected</span>
                <span>Connected as <strong>{status.login || 'your GitHub account'}</strong></span>
                <Check className="w-3.5 h-3.5 text-success" />
              </div>
              <div className="flex items-center gap-2 text-xs text-base-content/50">
                {status.daemonActive
                  ? <><span className="badge badge-success badge-xs">Daemon active</span> Watching {status.watcherCount} repo{status.watcherCount === 1 ? '' : 's'} across your projects{pollSeconds ? ` · polling every ${pollSeconds}s` : ''}.</>
                  : <><span className="badge badge-ghost badge-xs">Daemon stopped</span> The watcher daemon isn’t running — enable the issues-gateway service to start triaging.</>}
              </div>
              <p className="text-xs text-base-content/40">Add or remove watched repositories from a project’s <strong>Channels</strong> settings — each watcher files its issues into that project’s work queue.</p>
            </div>
          ) : (
            <div className="space-y-1.5">
              <div className="flex items-center gap-2 text-sm"><span className="badge badge-ghost badge-sm">Not connected</span></div>
              <p className="text-sm text-base-content/70">{error || 'GitHub isn’t connected yet.'}</p>
              <p className="text-xs text-base-content/50">Sign in with the GitHub CLI — run <code>gh auth login</code> in a terminal — then come back and refresh.</p>
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={() => { setLoading(true); refresh(); }} className="btn btn-sm btn-ghost gap-1" title="Refresh"><RefreshCw className="w-3.5 h-3.5" /> Refresh</button>
          </div>
        </div>
      </div>
    </>
  );
}

// A bound-chat row with an expandable "who can use it" member-permission editor.
function BoundChatRow({ api, chat, label, projectRoot, instance, icon: Icon, color, watch, assist, defaultAccess, busy, onUnbind }: {
  api: ChannelMemberApi | undefined;
  chat: string; label: string; projectRoot: string; instance?: string;
  icon: LucideIcon; color: string;
  watch: boolean; assist?: boolean; defaultAccess?: ChannelAccess;
  busy: boolean; onUnbind: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [members, setMembers] = useState<ChannelMember[] | null>(null);
  const [loadingM, setLoadingM] = useState(false);
  const [def, setDef] = useState<ChannelAccess | ''>(defaultAccess || '');
  const [err, setErr] = useState<string | null>(null);

  const loadMembers = async () => {
    if (!api) return;
    setLoadingM(true); setErr(null);
    try {
      const r = await api.listMembers(chat, instance);
      if (r.ok === false) { setErr(r.error || 'Could not list members'); setMembers([]); }
      else setMembers(r.members || []);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Failed to list members'); }
    finally { setLoadingM(false); }
  };
  const toggle = () => { const n = !expanded; setExpanded(n); if (n && members === null) loadMembers(); };
  const changeMember = async (id: string, access: ChannelAccess) => {
    if (!api) return;
    setMembers((l) => l ? l.map((m) => m.id === id ? { ...m, access } : m) : l);
    try { await api.setMemberAccess(projectRoot, chat, id, access, instance); } catch { /* ignore */ }
  };
  const changeDefault = async (access: ChannelAccess) => {
    if (!api) return;
    setDef(access);
    try { await api.setDefaultAccess(projectRoot, chat, access, instance); if (members) await loadMembers(); } catch { /* ignore */ }
  };

  return (
    <li className="bg-base-300 rounded-lg text-sm">
      <div className="flex items-center justify-between px-3 py-2">
        <button onClick={toggle} className="truncate flex items-center gap-1.5 flex-1 text-left">
          <ChevronRight className={`w-3 h-3 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          <Icon className={`w-3.5 h-3.5 ${color} shrink-0`} />
          <span className="truncate">{label || chat}</span>
          {assist && <span className="text-xs text-info/70">(assist)</span>}
          {!watch && <span className="text-xs text-base-content/40">(watch off)</span>}
        </button>
        <button onClick={onUnbind} disabled={busy} className="btn btn-ghost btn-xs text-error">Unbind</button>
      </div>
      {expanded && (
        <div className="px-3 pb-3 pt-1 space-y-2 border-t border-base-content/10">
          <div className="flex items-center gap-2 text-xs flex-wrap">
            <span className="text-base-content/60">Everyone in this chat:</span>
            <select className="select select-xs select-bordered" value={def} onChange={(e) => changeDefault(e.target.value as ChannelAccess)}>
              <option value="">— unset (all handled)</option>
              <option value="ignored">Ignored</option>
              <option value="read">Read-only</option>
              <option value="write">Read &amp; write</option>
            </select>
            <span className="text-base-content/40">default for anyone not set below</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-base-content/60">Members</span>
            <button onClick={loadMembers} disabled={loadingM} className="btn btn-ghost btn-xs gap-1">
              {loadingM ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Refresh
            </button>
          </div>
          {err && <div className="text-xs text-error">{err}</div>}
          {members && members.length > 0 ? (
            <ul className="space-y-1 max-h-48 overflow-auto pr-1">
              {members.map((m) => (
                <li key={m.id} className="flex items-center justify-between gap-2">
                  <span className="truncate text-xs">{m.name}</span>
                  <select className="select select-xs select-bordered" value={m.access} onChange={(e) => changeMember(m.id, e.target.value as ChannelAccess)}>
                    <option value="ignored">Ignored</option>
                    <option value="read">Read-only</option>
                    <option value="write">Read &amp; write</option>
                  </select>
                </li>
              ))}
            </ul>
          ) : members ? (
            <div className="text-xs text-base-content/50">No members listed.</div>
          ) : !loadingM ? (
            <div className="text-xs text-base-content/50">Refresh to load members.</div>
          ) : null}
        </div>
      )}
    </li>
  );
}

// ─── Channels (per-project: bind WhatsApp + Teams + Slack chats) ─────────────

function ChannelsSection({ projectRoot, projectName }: { projectRoot: string; projectName: string }) {
  const backend = useBackend();
  const [waBindings, setWaBindings] = useState<WhatsAppBinding[]>([]);
  const [teamsBindings, setTeamsBindings] = useState<TeamsBinding[]>([]);
  const [slackBindings, setSlackBindings] = useState<SlackBinding[]>([]);
  const [provider, setProvider] = useState<'whatsapp' | 'teams' | 'slack'>('slack');
  const [registry, setRegistry] = useState<Record<string, ProviderInstances>>({});
  // selected instance per provider
  const [selInst, setSelInst] = useState<Record<string, string>>({ whatsapp: 'default', teams: 'default', slack: 'default' });
  const [chats, setChats] = useState<{ id: string; label: string; kind?: string }[]>([]);
  const [selected, setSelected] = useState('');
  const [assist, setAssist] = useState(true);
  const [loadingChats, setLoadingChats] = useState(false);
  const [status, setStatus] = useState<{ ok: boolean; msg: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const instancesFor = (p: string): ChannelInstance[] => registry[p]?.instances || [{ id: 'default', name: 'Default' }];
  const inst = (p: string) => selInst[p] || registry[p]?.default || 'default';

  // Load the instance registry once; preselect each provider's default.
  useEffect(() => {
    (async () => {
      if (!backend.channels) return;
      try {
        const r = await backend.channels.listInstances();
        if (r.registry) {
          setRegistry(r.registry);
          setSelInst({
            whatsapp: r.registry.whatsapp?.default || 'default',
            teams: r.registry.teams?.default || 'default',
            slack: r.registry.slack?.default || 'default',
          });
        }
      } catch { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadBindings = async () => {
    if (backend.whatsapp) { try { const r = await backend.whatsapp.listBindings(projectRoot, inst('whatsapp')); setWaBindings(r.whatsapp || []); } catch { /* ignore */ } }
    if (backend.teams) { try { const r = await backend.teams.listBindings(projectRoot, inst('teams')); setTeamsBindings(r.teams || []); } catch { /* ignore */ } }
    if (backend.slack) { try { const r = await backend.slack.listBindings(projectRoot, inst('slack')); setSlackBindings(r.slack || []); } catch { /* ignore */ } }
  };
  useEffect(() => { loadBindings(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [projectRoot, selInst.whatsapp, selInst.teams, selInst.slack]);

  const refreshChats = async () => {
    setLoadingChats(true); setStatus(null); setSelected('');
    try {
      if (provider === 'whatsapp') {
        if (!backend.whatsapp) { setStatus({ ok: false, msg: 'WhatsApp is desktop-only.' }); return; }
        const r = await backend.whatsapp.listChats(inst('whatsapp'));
        if (r.ok === false) { setStatus({ ok: false, msg: r.error || 'Account not connected — set it up in Settings → Accounts / WhatsApp.' }); setChats([]); }
        else setChats((r.groups || []).map((c) => ({ id: c.jid, label: c.subject || c.jid })));
      } else if (provider === 'teams') {
        if (!backend.teams) { setStatus({ ok: false, msg: 'Teams is desktop-only.' }); return; }
        const r = await backend.teams.listChats(inst('teams'));
        if (r.ok === false) { setStatus({ ok: false, msg: r.error || 'Account not connected — set it up in Settings → Accounts / Teams.' }); setChats([]); }
        else setChats((r.chats || []).map((c) => ({ id: c.id, label: c.topic || c.id, kind: c.kind })));
      } else {
        if (!backend.slack) { setStatus({ ok: false, msg: 'Slack is desktop-only.' }); return; }
        const r = await backend.slack.listChats(inst('slack'));
        if (r.ok === false) { setStatus({ ok: false, msg: r.error || 'Account not connected — set it up in Settings → Accounts / Slack.' }); setChats([]); }
        else setChats((r.chats || []).map((c) => ({ id: c.id, label: (c.member ? '' : '(invite bot) ') + (c.topic || c.id) })));
      }
    } catch (e) {
      setStatus({ ok: false, msg: e instanceof Error ? e.message : 'Failed to list chats' });
    } finally {
      setLoadingChats(false);
    }
  };

  const doBind = async () => {
    if (!selected) return;
    const chat = chats.find((c) => c.id === selected);
    const label = (chat?.label || '').replace(/^\(invite bot\) /, '');
    const instance = inst(provider);
    setBusy(true); setStatus(null);
    try {
      if (provider === 'whatsapp') {
        if (!backend.whatsapp) return;
        await backend.whatsapp.bind(projectRoot, selected, { project: projectName, label, watch: true, instance });
      } else if (provider === 'teams') {
        if (!backend.teams) return;
        await backend.teams.bind(projectRoot, selected, { project: projectName, label, kind: chat?.kind, watch: true, assist, instance });
      } else {
        if (!backend.slack) return;
        await backend.slack.bind(projectRoot, selected, { project: projectName, label, watch: true, instance });
      }
      setSelected('');
      await loadBindings();
      setStatus({ ok: true, msg: `Bound "${label || selected}" to ${projectName}.` });
    } catch (e) {
      setStatus({ ok: false, msg: e instanceof Error ? e.message : 'Bind failed' });
    } finally {
      setBusy(false);
    }
  };

  const doUnbind = async (prov: 'whatsapp' | 'teams' | 'slack', chat: string) => {
    setBusy(true);
    try {
      if (prov === 'whatsapp' && backend.whatsapp) await backend.whatsapp.unbind(projectRoot, chat, inst('whatsapp'));
      if (prov === 'teams' && backend.teams) await backend.teams.unbind(projectRoot, chat, inst('teams'));
      if (prov === 'slack' && backend.slack) await backend.slack.unbind(projectRoot, chat, inst('slack'));
      await loadBindings();
    } catch { /* ignore */ }
    finally { setBusy(false); }
  };

  const boundIds = new Set(
    provider === 'whatsapp' ? waBindings.map((b) => b.chat)
      : provider === 'teams' ? teamsBindings.map((b) => b.chat)
      : slackBindings.map((b) => b.chat)
  );
  const available = chats.filter((c) => !boundIds.has(c.id));
  const hasBindings = waBindings.length + teamsBindings.length + slackBindings.length > 0;
  const providerLabel = provider === 'whatsapp' ? 'WhatsApp' : provider === 'teams' ? 'Teams' : 'Slack';
  const switchProvider = (p: 'whatsapp' | 'teams' | 'slack') => { setProvider(p); setChats([]); setSelected(''); setStatus(null); };
  const multi = instancesFor(provider).length > 1;

  return (
    <>
      <SectionHeader title="Channels" description="Connect this project to the outside world. Bind WhatsApp, Teams, and Slack chats (published to this project's pub/sub so agents can watch and reply), and watch GitHub repositories whose issues are filed into this project's work queue. Manage accounts under Settings → Accounts." />
      <div className="card bg-base-200">
        <div className="card-body py-4 space-y-4">
          {/* Bound chats (for the selected account per provider) */}
          <div>
            <div className="text-xs font-medium text-base-content/60 mb-1">Bound chats</div>
            {!hasBindings ? (
              <div className="text-sm text-base-content/50">No chats bound yet for the selected accounts.</div>
            ) : (
              <ul className="space-y-1">
                {waBindings.map((b) => (
                  <BoundChatRow key={`wa-${b.chat}`} api={backend.whatsapp} chat={b.chat} label={b.label} projectRoot={projectRoot} instance={inst('whatsapp')}
                    icon={MessageCircle} color="text-success" watch={b.watch} defaultAccess={b.defaultAccess} busy={busy}
                    onUnbind={() => doUnbind('whatsapp', b.chat)} />
                ))}
                {teamsBindings.map((b) => (
                  <BoundChatRow key={`teams-${b.chat}`} api={backend.teams} chat={b.chat} label={b.label} projectRoot={projectRoot} instance={inst('teams')}
                    icon={Users} color="text-info" watch={b.watch} assist={b.assist} defaultAccess={b.defaultAccess} busy={busy}
                    onUnbind={() => doUnbind('teams', b.chat)} />
                ))}
                {slackBindings.map((b) => (
                  <BoundChatRow key={`slack-${b.chat}`} api={backend.slack} chat={b.chat} label={b.label} projectRoot={projectRoot} instance={inst('slack')}
                    icon={Hash} color="text-warning" watch={b.watch} defaultAccess={b.defaultAccess} busy={busy}
                    onUnbind={() => doUnbind('slack', b.chat)} />
                ))}
              </ul>
            )}
          </div>

          {/* Add a binding */}
          <div className="space-y-2">
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-xs font-medium text-base-content/60 mr-1">Add from</span>
              <button onClick={() => switchProvider('whatsapp')} className={`btn btn-xs gap-1 ${provider === 'whatsapp' ? 'btn-primary' : 'btn-ghost'}`}><MessageCircle className="w-3 h-3" /> WhatsApp</button>
              <button onClick={() => switchProvider('teams')} className={`btn btn-xs gap-1 ${provider === 'teams' ? 'btn-primary' : 'btn-ghost'}`}><Users className="w-3 h-3" /> Teams</button>
              <button onClick={() => switchProvider('slack')} className={`btn btn-xs gap-1 ${provider === 'slack' ? 'btn-primary' : 'btn-ghost'}`}><Hash className="w-3 h-3" /> Slack</button>
              {multi && (
                <select
                  className="select select-xs select-bordered ml-1"
                  value={inst(provider)}
                  onChange={(e) => { setSelInst((s) => ({ ...s, [provider]: e.target.value })); setChats([]); setSelected(''); setStatus(null); }}
                  title="Account"
                >
                  {instancesFor(provider).map((i) => <option key={i.id} value={i.id}>{i.name}{registry[provider]?.default === i.id ? ' (default)' : ''}</option>)}
                </select>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={refreshChats} disabled={loadingChats} className="btn btn-sm btn-ghost gap-1" title={`Refresh your ${providerLabel} chats`}>
                {loadingChats ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Refresh chats
              </button>
              <select
                className="select select-sm select-bordered flex-1 min-w-[12rem]"
                value={selected}
                onChange={(e) => setSelected(e.target.value)}
                disabled={available.length === 0}
              >
                <option value="">{available.length ? 'Select a chat to bind…' : `Refresh to list your ${providerLabel} chats`}</option>
                {available.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
              </select>
              <button onClick={doBind} disabled={!selected || busy} className="btn btn-sm btn-primary gap-1"><Plus className="w-3.5 h-3.5" /> Bind</button>
            </div>
            {provider === 'teams' && (
              <label className="flex items-center gap-2 text-xs text-base-content/60 cursor-pointer">
                <input type="checkbox" className="checkbox checkbox-xs" checked={assist} onChange={(e) => setAssist(e.target.checked)} />
                Assistant mode — reply to your own messages (personal chat with Claude)
              </label>
            )}
            {provider === 'slack' && (
              <p className="text-xs text-base-content/50">The bot only sees channels it's been invited to — <code>/invite @AtelierCode</code> in Slack first. Channels needing an invite are marked "(invite bot)".</p>
            )}
          </div>

          {status && <div className={`alert ${status.ok ? 'alert-success' : 'alert-error'} py-2 text-sm`}>{status.msg}</div>}
        </div>
      </div>

      {/* GitHub issues → this project's work queue (draft PRs). Watchers are repo + policy. */}
      <GitHubSection projectRoot={projectRoot} projectName={projectName} />
    </>
  );
}

// ─── Project Settings ────────────────────────────────────────────────────────

function ProjectSettings({ section }: { section: string }) {
  const project = useProjectStore((state) => state.currentProject);
  const updateProjectInStore = useProjectStore((state) => state.updateProject);
  const backend = useBackend();
  const availableShells = useAvailableShells();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const [editedStatus, setEditedStatus] = useState<string>('active');
  const [editedIcon, setEditedIcon] = useState<string | null>(null);
  const [editedColor, setEditedColor] = useState<string | null>(null);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiGeneratedDetails, setAiGeneratedDetails] = useState<AIProjectDetails>({ name: '', description: '' });
  const [editedAgentType, setEditedAgentType] = useState<string>('');
  // Per-project Claude identity ('' = use the global default). savedClaudeIdentity
  // mirrors what's currently in .ateliercode/config.json for change detection.
  const [editedClaudeIdentity, setEditedClaudeIdentity] = useState<string>('');
  const [savedClaudeIdentity, setSavedClaudeIdentity] = useState<string>('');
  const [claudeIdentities, setClaudeIdentities] = useState<ClaudeIdentity[]>([]);
  // Per-project Copilot identity ('' = use the global default).
  const [editedCopilotIdentity, setEditedCopilotIdentity] = useState<string>('');
  const [savedCopilotIdentity, setSavedCopilotIdentity] = useState<string>('');
  const [copilotIdentities, setCopilotIdentities] = useState<CopilotIdentity[]>([]);
  const [editedAutoLaunch, setEditedAutoLaunch] = useState(true);
  const [editedAgentFlags, setEditedAgentFlags] = useState<string[]>([]);
  const [editedCustomAgentFlags, setEditedCustomAgentFlags] = useState('');
  const [editedPreferredModel, setEditedPreferredModel] = useState<string>('');
  const [editedNotificationSound, setEditedNotificationSound] = useState<NotificationSoundId>('chime');
  const [editedDefaultShell, setEditedDefaultShell] = useState<ShellType>('powershell');
  const [editedRunConfigs, setEditedRunConfigs] = useState<RunConfiguration[]>([]);
  const [editedDefaultConfigId, setEditedDefaultConfigId] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [isDetecting, setIsDetecting] = useState(false);

  useEffect(() => {
    if (!project) return;

    setEditedName(project.name);
    setEditedDescription(project.prd_content || '');
    setEditedStatus(project.status);
    setEditedIcon(project.icon || null);
    setEditedColor(project.color || null);
    setEditedAgentType(project.agent_type || '');

    // Load from .ateliercode/ files first, fall back to settings blob
    (async () => {
      // Config: agent + terminal settings
      try {
        const raw = await backend.files.readAteliercodeConfig(project.id);
        if (raw) {
          const cfg = JSON.parse(raw);
          const ident = typeof cfg.claudeIdentity === 'string' ? cfg.claudeIdentity : '';
          setEditedClaudeIdentity(ident);
          setSavedClaudeIdentity(ident);
          const cpIdent = typeof cfg.copilotIdentity === 'string' ? cfg.copilotIdentity : '';
          setEditedCopilotIdentity(cpIdent);
          setSavedCopilotIdentity(cpIdent);
          if (cfg.agent) {
            if (cfg.agent.type) setEditedAgentType(cfg.agent.type);
            if (cfg.agent.autoLaunch !== undefined) setEditedAutoLaunch(cfg.agent.autoLaunch);
            if (cfg.agent.flags) setEditedAgentFlags(cfg.agent.flags);
            if (typeof cfg.agent.customFlags === 'string') setEditedCustomAgentFlags(cfg.agent.customFlags);
          }
          if (cfg.terminal) {
            if (cfg.terminal.defaultShell) setEditedDefaultShell(cfg.terminal.defaultShell);
            if (cfg.terminal.notificationSound) setEditedNotificationSound(cfg.terminal.notificationSound);
          }
        } else {
          // Fall back to blob
          const termSettings = getProjectTerminalSettings(project.settings);
          setEditedAutoLaunch(termSettings.autoLaunchAgent);
          setEditedAgentFlags(termSettings.agentFlags);
          setEditedCustomAgentFlags(termSettings.customAgentFlags);
          setEditedPreferredModel(termSettings.preferredModel ?? '');
          setEditedNotificationSound(termSettings.notificationSound);
          setEditedDefaultShell(termSettings.defaultShell);
          setEditedClaudeIdentity('');
          setSavedClaudeIdentity('');
          setEditedCopilotIdentity('');
          setSavedCopilotIdentity('');
        }
      } catch {
        const termSettings = getProjectTerminalSettings(project.settings);
        setEditedAutoLaunch(termSettings.autoLaunchAgent);
        setEditedAgentFlags(termSettings.agentFlags);
        setEditedCustomAgentFlags(termSettings.customAgentFlags);
        setEditedPreferredModel(termSettings.preferredModel ?? '');
        setEditedNotificationSound(termSettings.notificationSound);
        setEditedDefaultShell(termSettings.defaultShell);
        setEditedClaudeIdentity('');
        setSavedClaudeIdentity('');
        setEditedCopilotIdentity('');
        setSavedCopilotIdentity('');
      }

      // Identity dropdown options (app-level registry; cheap DB read)
      try {
        setClaudeIdentities(await backend.identity.list());
      } catch {
        setClaudeIdentities([]);
      }
      try {
        setCopilotIdentities(await backend.copilotIdentity.list());
      } catch {
        setCopilotIdentities([]);
      }

      // Runners: run configurations
      try {
        const raw = await backend.files.readAteliercodeRunners(project.id);
        if (raw) {
          const runners = JSON.parse(raw);
          if (runners.configs && Array.isArray(runners.configs)) {
            let counter = 0;
            const configs = runners.configs.map((c: { name: string; command: string; shell?: string }) => ({
              id: Date.now().toString(36) + (++counter).toString(36),
              name: c.name,
              command: c.command,
              shell: c.shell,
            }));
            setEditedRunConfigs(configs);
            const defaultConfig = runners.default
              ? configs.find((c: { name: string }) => c.name === runners.default)
              : configs[0];
            setEditedDefaultConfigId(defaultConfig?.id ?? null);
          }
        } else {
          const runnerSettings = getProjectRunnerSettings(project.settings);
          setEditedRunConfigs(runnerSettings.configs);
          setEditedDefaultConfigId(runnerSettings.defaultConfigId);
        }
      } catch {
        const runnerSettings = getProjectRunnerSettings(project.settings);
        setEditedRunConfigs(runnerSettings.configs);
        setEditedDefaultConfigId(runnerSettings.defaultConfigId);
      }
    })();
  }, [project, backend]);

  useEffect(() => {
    if (!project) return;
    const nameChanged = editedName !== project.name;
    const descChanged = editedDescription !== (project.prd_content || '');
    const statusChanged = editedStatus !== project.status;
    const iconChanged = editedIcon !== (project.icon || null);
    const colorChanged = editedColor !== (project.color || null);
    const agentChanged = editedAgentType !== (project.agent_type || '');
    const termSettings = getProjectTerminalSettings(project.settings);
    const autoLaunchChanged = editedAutoLaunch !== termSettings.autoLaunchAgent;
    const flagsChanged = JSON.stringify([...editedAgentFlags].sort()) !== JSON.stringify([...termSettings.agentFlags].sort());
    const customFlagsChanged = editedCustomAgentFlags !== termSettings.customAgentFlags;
    const preferredModelChanged = editedPreferredModel !== (termSettings.preferredModel ?? '');
    const soundChanged = editedNotificationSound !== termSettings.notificationSound;
    const shellChanged = editedDefaultShell !== termSettings.defaultShell;
    const runnerSettings = getProjectRunnerSettings(project.settings);
    const runConfigsChanged = JSON.stringify(editedRunConfigs) !== JSON.stringify(runnerSettings.configs);
    const defaultConfigChanged = editedDefaultConfigId !== runnerSettings.defaultConfigId;
    const identityChanged = editedClaudeIdentity !== savedClaudeIdentity;
    const copilotIdentityChanged = editedCopilotIdentity !== savedCopilotIdentity;
    setHasChanges(nameChanged || descChanged || statusChanged || iconChanged || colorChanged || agentChanged || autoLaunchChanged || flagsChanged || customFlagsChanged || preferredModelChanged || soundChanged || shellChanged || runConfigsChanged || defaultConfigChanged || identityChanged || copilotIdentityChanged);
  }, [editedName, editedDescription, editedStatus, editedIcon, editedColor, editedAgentType, editedAutoLaunch, editedAgentFlags, editedCustomAgentFlags, editedPreferredModel, editedNotificationSound, editedDefaultShell, editedRunConfigs, editedDefaultConfigId, editedClaudeIdentity, savedClaudeIdentity, editedCopilotIdentity, savedCopilotIdentity, project]);

  if (!project) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center py-16 text-base-content/60">
        <SettingsIcon className="w-12 h-12 opacity-20 mb-4" />
        <p className="text-lg font-medium">No Project Selected</p>
        <p className="text-sm">Open a project to configure its settings.</p>
      </div>
    );
  }

  const handleRegenerateWithAI = async () => {
    setIsRegenerating(true);
    setShowAIModal(true);
    try {
      const details = await backend.projects.generateDetails(project.root_path) as AIProjectDetails;
      setAiGeneratedDetails(details);
    } catch (error) {
      logger.error('SettingsModal', 'Failed to generate project details:', error);
      setShowAIModal(false);
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleApplyAIDetails = (details: AIProjectDetails) => {
    setEditedName(details.name);
    setEditedDescription(details.description);
    if (details.icon) {
      setEditedIcon(details.icon);
    }
    setHasChanges(true);
  };

  const handleSaveChanges = async () => {
    if (!project) return;
    setIsSaving(true);
    try {
      const terminalSettings = setProjectTerminalSettings(project.settings, {
        autoLaunchAgent: editedAutoLaunch,
        agentFlags: editedAgentFlags,
        customAgentFlags: editedCustomAgentFlags,
        preferredModel: editedPreferredModel || undefined,
        notificationSound: editedNotificationSound,
        defaultShell: editedDefaultShell,
      });
      const mergedSettings = setProjectRunnerSettings(terminalSettings, {
        configs: editedRunConfigs,
        defaultConfigId: editedDefaultConfigId,
      });
      await backend.projects.update(project.id, {
        name: editedName,
        prd_content: editedDescription,
        status: editedStatus,
        icon: editedIcon || undefined,
        color: editedColor || undefined,
        agent_type: editedAgentType || undefined,
        settings: mergedSettings,
      });
      await updateProjectInStore(project.id, {
        name: editedName,
        prd_content: editedDescription,
        status: editedStatus,
        icon: editedIcon || undefined,
        color: editedColor || undefined,
        agent_type: editedAgentType || undefined,
        settings: mergedSettings,
      });
      // Sync to .ateliercode/ files (fire-and-forget). Merge over the existing
      // config.json rather than rebuilding it, so keys this panel doesn't edit
      // (`work`, anything user-added) survive a save.
      try {
        let existingConfig: Record<string, unknown> = {};
        try {
          const raw = await backend.files.readAteliercodeConfig(project.id);
          if (raw) existingConfig = JSON.parse(raw);
        } catch { /* unreadable/missing — start fresh */ }
        const config: Record<string, unknown> = {
          ...existingConfig,
          $schema: 'https://ateliercode.dev/schemas/config.v1.json',
          agent: {
            type: editedAgentType,
            autoLaunch: editedAutoLaunch,
            flags: editedAgentFlags,
            ...(editedCustomAgentFlags.trim() ? { customFlags: editedCustomAgentFlags.trim() } : {}),
          },
          terminal: { defaultShell: editedDefaultShell, notificationSound: editedNotificationSound },
        };
        if (editedClaudeIdentity) config.claudeIdentity = editedClaudeIdentity;
        else delete config.claudeIdentity;
        if (editedCopilotIdentity) config.copilotIdentity = editedCopilotIdentity;
        else delete config.copilotIdentity;
        const runners = {
          $schema: 'https://ateliercode.dev/schemas/runners.v1.json',
          configs: editedRunConfigs.map((c: { name: string; command: string }) => ({ name: c.name, command: c.command })),
          default: editedRunConfigs.find((c: { id: string }) => c.id === editedDefaultConfigId)?.name ?? null,
        };
        await backend.files.initAteliercodeConfig(
          project.id,
          JSON.stringify(config, null, 2),
          JSON.stringify(runners, null, 2),
        );
        setSavedClaudeIdentity(editedClaudeIdentity);
        setSavedCopilotIdentity(editedCopilotIdentity);
      } catch {
        // Non-fatal — DB save already succeeded above
      }

      setHasChanges(false);
      useUIStore.getState().closeSettings();
    } catch (error) {
      logger.error('SettingsModal', 'Failed to save changes:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setEditedName(project.name);
    setEditedDescription(project.prd_content || '');
    setEditedStatus(project.status);
    setEditedIcon(project.icon || null);
    setEditedColor(project.color || null);
    setEditedAgentType(project.agent_type || '');
    const termSettings = getProjectTerminalSettings(project.settings);
    setEditedAutoLaunch(termSettings.autoLaunchAgent);
    setEditedAgentFlags(termSettings.agentFlags);
    setEditedCustomAgentFlags(termSettings.customAgentFlags);
    setEditedPreferredModel(termSettings.preferredModel ?? '');
    setEditedNotificationSound(termSettings.notificationSound);
    setEditedDefaultShell(termSettings.defaultShell);
    const runnerSettings = getProjectRunnerSettings(project.settings);
    setEditedRunConfigs(runnerSettings.configs);
    setEditedDefaultConfigId(runnerSettings.defaultConfigId);
    setEditedClaudeIdentity(savedClaudeIdentity);
    setEditedCopilotIdentity(savedCopilotIdentity);
    setHasChanges(false);
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageError(null);

    if (file.size > 5 * 1024 * 1024) {
      setImageError('Image must be under 5 MB.');
      return;
    }

    try {
      const dataUrl = await resizeImageToDataUrl(file, 128);
      setEditedIcon(dataUrl);
      setHasChanges(true);
    } catch {
      setImageError('Failed to process image.');
    }

    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  return (
    <>
      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto p-6 pr-12">
        <div key={section} className="animate-section-enter space-y-6">

          {/* ── General ── */}
          {section === 'project-general' && (
            <>
              <div className="flex items-center justify-between">
                <SectionHeader title="General" description="Basic project information and status." />
                <button onClick={handleRegenerateWithAI} disabled={isRegenerating} className="btn btn-primary btn-sm gap-2 flex-shrink-0">
                  {isRegenerating ? <><Loader2 className="w-4 h-4 animate-spin" />Generating...</> : <><Sparkles className="w-4 h-4" />Regenerate with AI</>}
                </button>
              </div>

              <div className="card bg-base-200">
                <div className="card-body py-4 space-y-4">
                  <div className="form-control">
                    <label className="label"><span className="label-text font-medium">Project Name</span></label>
                    <input type="text" className="input input-bordered w-full" value={editedName} onChange={(e) => setEditedName(e.target.value)} placeholder="Enter project name" />
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text font-medium">Description</span></label>
                    <textarea className="textarea textarea-bordered w-full" value={editedDescription} onChange={(e) => setEditedDescription(e.target.value)} placeholder="Enter project description" rows={3} />
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text font-medium">Active Status</span></label>
                    <label className="label cursor-pointer justify-start gap-4">
                      <input type="checkbox" className="toggle toggle-success" checked={editedStatus === 'active'} onChange={(e) => setEditedStatus(e.target.checked ? 'active' : 'archived')} />
                      <div>
                        <span className="label-text font-medium">{editedStatus === 'active' ? 'Active' : 'Inactive'}</span>
                        <div className="label-text-alt text-base-content/60 mt-1">
                          {editedStatus === 'active' ? 'Project appears on home page' : 'Project is hidden from default view'}
                        </div>
                      </div>
                    </label>
                  </div>
                  <div className="form-control">
                    <label className="label"><span className="label-text font-medium">Project Path</span></label>
                    <input type="text" className="input input-bordered w-full font-mono text-sm" value={project.root_path} disabled />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── Appearance ── */}
          {section === 'project-appearance' && (
            <>
              <SectionHeader title="Appearance" description="Customize how this project looks across the app." />

              {/* Project Icon */}
              <div className="card bg-base-200">
                <div className="card-body py-5">
                  <div className="flex items-start gap-6">
                    {/* Large icon preview */}
                    <div
                      className="relative group cursor-pointer flex-shrink-0"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <div className="w-20 h-20 rounded-2xl bg-base-300 flex items-center justify-center overflow-hidden border-2 border-dashed border-base-content/10 group-hover:border-primary/40 transition-all">
                        {isImageIcon(editedIcon) ? (
                          <img src={editedIcon!} alt="Project icon" className="w-full h-full object-cover" />
                        ) : editedIcon ? (
                          <span className="text-4xl">{editedIcon}</span>
                        ) : (
                          <div className="flex flex-col items-center gap-1 text-base-content/25">
                            <ImageIcon className="w-6 h-6" />
                            <span className="text-[10px] font-medium">Upload</span>
                          </div>
                        )}
                      </div>
                      {/* Hover overlay */}
                      <div className="absolute inset-0 rounded-2xl bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity">
                        <Upload className="w-5 h-5 text-white" />
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif,image/x-icon"
                        className="hidden"
                        onChange={handleImageUpload}
                      />
                    </div>

                    {/* Right side: info + emoji grid */}
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium mb-1">Project Icon</h4>
                      <p className="text-xs text-base-content/50 mb-3">
                        Upload a logo image or pick an emoji. Click the preview to upload.
                      </p>

                      {imageError && (
                        <div className="text-xs text-error mb-2">{imageError}</div>
                      )}

                      {/* Emoji grid */}
                      <div className="flex flex-wrap gap-1.5">
                        {EMOJIS.map((emoji) => (
                          <button
                            key={emoji}
                            type="button"
                            onClick={() => { setEditedIcon(emoji); setHasChanges(true); setImageError(null); }}
                            className={`w-9 h-9 rounded-lg flex items-center justify-center text-lg transition-all ${
                              editedIcon === emoji
                                ? 'bg-primary/20 ring-1 ring-primary scale-105'
                                : 'hover:bg-base-300 hover:scale-105'
                            }`}
                          >
                            {emoji}
                          </button>
                        ))}
                      </div>

                      {editedIcon && (
                        <button
                          type="button"
                          onClick={() => { setEditedIcon(null); setHasChanges(true); setImageError(null); }}
                          className="btn btn-ghost btn-xs mt-3 text-base-content/50"
                        >
                          <X className="w-3 h-3" /> Remove icon
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Project Color */}
              <div className="card bg-base-200">
                <div className="card-body py-5">
                  <h4 className="font-medium mb-1">Project Color</h4>
                  <p className="text-xs text-base-content/50 mb-4">
                    Accent color used in the sidebar and throughout the app.
                  </p>
                  <div className="flex flex-wrap gap-2.5">
                    {COLORS.map((color) => (
                      <button
                        key={color.name}
                        type="button"
                        onClick={() => { setEditedColor(color.name); setHasChanges(true); }}
                        className={`w-9 h-9 rounded-full ${color.bg} transition-all ${
                          editedColor === color.name
                            ? 'ring-2 ring-offset-2 ring-offset-base-100 ring-primary scale-110'
                            : 'hover:scale-110'
                        }`}
                        title={color.name}
                      />
                    ))}
                    {editedColor && (
                      <button
                        type="button"
                        onClick={() => { setEditedColor(null); setHasChanges(true); }}
                        className="h-9 px-3 rounded-full border border-dashed border-base-content/20 text-xs text-base-content/50 hover:border-base-content/40 transition-colors"
                      >
                        None
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── AI Agent ── */}
          {section === 'project-agent' && (
            <>
              <SectionHeader title="AI Agent" description="Configure the AI agent used for this project." />
              <div className="card bg-base-200">
                <div className="card-body py-4 space-y-4">
                  <div className="form-control">
                    <label className="label"><span className="label-text font-medium">Agent Type</span></label>
                    <select
                      value={editedAgentType}
                      onChange={(e) => {
                        setEditedAgentType(e.target.value);
                        setEditedAgentFlags([]);
                        setEditedCustomAgentFlags('');
                        setHasChanges(true);
                      }}
                      className="select select-bordered w-full"
                    >
                      <option value="">Disabled (No Agent)</option>
                      {Object.entries(AGENT_CLI_CONFIG).map(([key, config]) => (
                        <option key={key} value={key}>{config.displayName}</option>
                      ))}
                    </select>
                    <label className="label">
                      <span className="label-text-alt text-base-content/60">
                        The AI agent CLI used when launching terminals for this project
                      </span>
                    </label>
                  </div>

                  {editedAgentType && AGENT_CLI_CONFIG[editedAgentType] && (
                    <div className="space-y-3 pl-2 border-l-2 border-primary/20 ml-2">
                      <label className="label cursor-pointer justify-start gap-4">
                        <input
                          type="checkbox"
                          checked={editedAutoLaunch}
                          onChange={(e) => { setEditedAutoLaunch(e.target.checked); setHasChanges(true); }}
                          className="toggle toggle-primary"
                        />
                        <div>
                          <span className="label-text font-medium">Auto-Launch Agent</span>
                          <p className="text-xs text-base-content/60">
                            Automatically start {AGENT_CLI_CONFIG[editedAgentType].displayName} when opening a new terminal
                          </p>
                        </div>
                      </label>

                      {AGENT_CLI_CONFIG[editedAgentType].flags.length > 0 && (
                        <div className="bg-base-300/50 rounded-lg p-3">
                          <h4 className="font-medium text-sm mb-2">Launch Flags</h4>
                          {AGENT_CLI_CONFIG[editedAgentType].flags.map((flag) => (
                            <label key={flag.id} className="label cursor-pointer justify-start gap-3 py-1">
                              <input
                                type="checkbox"
                                checked={editedAgentFlags.includes(flag.id)}
                                onChange={(e) => {
                                  const updated = e.target.checked
                                    ? [...editedAgentFlags, flag.id]
                                    : editedAgentFlags.filter((id) => id !== flag.id);
                                  setEditedAgentFlags(updated);
                                  setHasChanges(true);
                                }}
                                className="checkbox checkbox-sm checkbox-primary"
                              />
                              <div>
                                <span className="label-text text-sm">{flag.label}</span>
                                <p className="text-xs text-base-content/60">{flag.description}</p>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}

                      {AGENT_CLI_CONFIG[editedAgentType].modelFlag && (
                        <div className="bg-base-300/50 rounded-lg p-3">
                          <AgentModelPicker
                            agentType={editedAgentType}
                            value={editedPreferredModel}
                            onChange={(v) => { setEditedPreferredModel(v); setHasChanges(true); }}
                            showFallback
                          />
                        </div>
                      )}

                      <div className="bg-base-300/50 rounded-lg p-3">
                        <div className="flex items-baseline justify-between mb-2">
                          <h4 className="font-medium text-sm">Additional Flags</h4>
                          <span className="text-xs text-base-content/60">appended verbatim</span>
                        </div>
                        <input
                          type="text"
                          value={editedCustomAgentFlags}
                          onChange={(e) => { setEditedCustomAgentFlags(e.target.value); setHasChanges(true); }}
                          placeholder="e.g. --model gpt-5-codex --cd ."
                          className="input input-bordered input-sm w-full font-mono"
                        />
                        <p className="text-xs text-base-content/50 mt-2">
                          Anything you'd type after <code>{AGENT_CLI_CONFIG[editedAgentType].command}</code> on the command line.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Per-project Claude identity (isolated CLAUDE_CONFIG_DIR login).
                  Applies to ALL terminals in the project — not just the agent —
                  so it lives outside the agent-type conditional above. */}
              <div className="card bg-base-200">
                <div className="card-body py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Key className="w-4 h-4 text-primary" />
                    <h4 className="font-medium">Claude Identity</h4>
                  </div>
                  <p className="text-xs text-base-content/50 mb-3">
                    Which Claude login this project's terminals (and the agents they launch) run
                    under. Manage identities in App Settings → Claude Identities.
                  </p>
                  <select
                    value={editedClaudeIdentity}
                    onChange={(e) => { setEditedClaudeIdentity(e.target.value); setHasChanges(true); }}
                    className="select select-bordered w-full"
                  >
                    <option value="">
                      Global default{(() => { const d = claudeIdentities.find((i) => i.isDefault); return d ? ` (${d.name})` : ''; })()}
                    </option>
                    {claudeIdentities.map((i) => (
                      <option key={i.name} value={i.name}>
                        {i.name}{i.email ? ` — ${i.email}` : ''}
                      </option>
                    ))}
                  </select>
                  {editedClaudeIdentity && !claudeIdentities.some((i) => i.name === editedClaudeIdentity) && (
                    <p className="text-xs text-warning mt-2">
                      "{editedClaudeIdentity}" isn't a known identity — terminals will fall back to
                      the global default until it's added in App Settings → Claude Identities.
                    </p>
                  )}
                  {claudeIdentities.length === 0 && (
                    <p className="text-xs text-base-content/50 mt-2">
                      No identities defined yet — add one in App Settings → Claude Identities to
                      isolate a work (SSO) login from your personal account.
                    </p>
                  )}
                </div>
              </div>

              {/* Per-project Copilot identity (isolated COPILOT_HOME login).
                  Like the Claude card above, applies to ALL terminals in the
                  project (the Copilot CLI picks it up via COPILOT_HOME). */}
              <div className="card bg-base-200">
                <div className="card-body py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Github className="w-4 h-4 text-primary" />
                    <h4 className="font-medium">Copilot Identity</h4>
                  </div>
                  <p className="text-xs text-base-content/50 mb-3">
                    Which GitHub Copilot login this project's terminals use (personal vs. work).
                    Manage identities in App Settings → Copilot Identities.
                  </p>
                  <select
                    value={editedCopilotIdentity}
                    onChange={(e) => { setEditedCopilotIdentity(e.target.value); setHasChanges(true); }}
                    className="select select-bordered w-full"
                  >
                    <option value="">
                      Global default{(() => { const d = copilotIdentities.find((i) => i.isDefault); return d ? ` (${d.name})` : ''; })()}
                    </option>
                    {copilotIdentities.map((i) => (
                      <option key={i.name} value={i.name}>
                        {i.name}{i.account ? ` — ${i.account}` : ''}
                      </option>
                    ))}
                  </select>
                  {editedCopilotIdentity && !copilotIdentities.some((i) => i.name === editedCopilotIdentity) && (
                    <p className="text-xs text-warning mt-2">
                      "{editedCopilotIdentity}" isn't a known identity — terminals will fall back to
                      the global default until it's added in App Settings → Copilot Identities.
                    </p>
                  )}
                  {copilotIdentities.length === 0 && (
                    <p className="text-xs text-base-content/50 mt-2">
                      No Copilot identities defined yet — add one in App Settings → Copilot
                      Identities to isolate a work login from your personal account.
                    </p>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ── Terminal & Runner ── */}
          {section === 'project-terminal' && (
            <>
              <SectionHeader title="Terminal & Runner" description="Configure shell, notification sounds, and run configurations for this project." />

              {/* Default Shell */}
              <div className="card bg-base-200">
                <div className="card-body py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Play className="w-4 h-4 text-primary" />
                    <h4 className="font-medium">Default Shell</h4>
                  </div>
                  <p className="text-xs text-base-content/50 mb-3">
                    Choose which shell to use when opening new terminals for this project.
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {availableShells.map((shell) => (
                      <button
                        key={shell.id}
                        type="button"
                        onClick={() => { setEditedDefaultShell(shell.id as ShellType); setHasChanges(true); }}
                        className={`btn btn-sm justify-start gap-2 ${
                          editedDefaultShell === shell.id
                            ? 'btn-primary'
                            : 'btn-ghost border border-base-300'
                        }`}
                      >
                        {editedDefaultShell === shell.id && <Check className="w-3.5 h-3.5" />}
                        {shell.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Notifications */}
              <div className="card bg-base-200">
                <div className="card-body py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="w-4 h-4 text-primary" />
                    <h4 className="font-medium">Completion Notifications</h4>
                  </div>
                  <p className="text-xs text-base-content/50 mb-3">
                    Play a sound when terminal output stops (command finishes).
                  </p>
                  <div className="flex items-center gap-3">
                    <select
                      value={editedNotificationSound}
                      onChange={(e) => { setEditedNotificationSound(e.target.value as NotificationSoundId); setHasChanges(true); }}
                      className="select select-bordered select-sm flex-1 max-w-xs"
                    >
                      {NOTIFICATION_SOUNDS.map((s) => (
                        <option key={s.id} value={s.id}>{s.label}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => previewNotificationSound(editedNotificationSound)}
                      disabled={editedNotificationSound === 'none'}
                      className="btn btn-ghost btn-sm"
                    >
                      Preview
                    </button>
                  </div>
                </div>
              </div>

              {/* Run Configurations */}
              <div className="card bg-base-200">
                <div className="card-body py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Play className="w-4 h-4 text-primary" />
                    <h4 className="font-medium">Run Configurations</h4>
                  </div>
                  <p className="text-xs text-base-content/50 mb-3">
                    Configure commands to quickly run your project. Star a configuration to make it the default.
                  </p>
                  <div className="flex flex-col gap-2">
                    {editedRunConfigs.map((config, index) => (
                      <div key={config.id} className="flex items-center gap-2 bg-base-300/50 rounded-lg px-3 py-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditedDefaultConfigId(editedDefaultConfigId === config.id ? null : config.id);
                            setHasChanges(true);
                          }}
                          className={`btn btn-ghost btn-xs btn-square shrink-0 ${editedDefaultConfigId === config.id ? 'text-amber-500' : 'text-base-content/30'}`}
                          title={editedDefaultConfigId === config.id ? 'Default configuration' : 'Set as default'}
                        >
                          <Star className={`w-3.5 h-3.5 ${editedDefaultConfigId === config.id ? 'fill-amber-500' : ''}`} />
                        </button>
                        <input
                          type="text"
                          value={config.name}
                          onChange={(e) => {
                            const updated = [...editedRunConfigs];
                            updated[index] = { ...config, name: e.target.value };
                            setEditedRunConfigs(updated);
                            setHasChanges(true);
                          }}
                          placeholder="Name"
                          className="input input-bordered input-sm w-32 shrink-0"
                        />
                        <input
                          type="text"
                          value={config.command}
                          onChange={(e) => {
                            const updated = [...editedRunConfigs];
                            updated[index] = { ...config, command: e.target.value };
                            setEditedRunConfigs(updated);
                            setHasChanges(true);
                          }}
                          placeholder="Command (e.g. npm run dev)"
                          className="input input-bordered input-sm flex-1 font-mono text-xs"
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const updated = editedRunConfigs.filter((_, i) => i !== index);
                            setEditedRunConfigs(updated);
                            if (editedDefaultConfigId === config.id) setEditedDefaultConfigId(null);
                            setHasChanges(true);
                          }}
                          className="btn btn-ghost btn-xs btn-square text-error/60 hover:text-error shrink-0"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          const newConfig: RunConfiguration = {
                            id: Date.now().toString(36),
                            name: '',
                            command: '',
                          };
                          setEditedRunConfigs([...editedRunConfigs, newConfig]);
                          setHasChanges(true);
                        }}
                        className="btn btn-ghost btn-sm gap-1"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add
                      </button>
                      <button
                        type="button"
                        disabled={isDetecting}
                        onClick={async () => {
                          if (!project?.id) return;
                          setIsDetecting(true);
                          try {
                            const readFile = (path: string) => backend.files.readFile(project.id, path);
                            const rootNodes = await backend.files.getFolderChildren(project.id, project.root_path);
                            const ctx: DetectionContext = { readFile, rootFiles: rootNodes.map((n) => n.name) };
                            const detected = await detectRunConfigurations(ctx);

                            if (detected.length > 0) {
                              const existingCommands = new Set(editedRunConfigs.map((c) => c.command));
                              const newConfigs = detected.filter((c) => !existingCommands.has(c.command));
                              if (newConfigs.length > 0) {
                                setEditedRunConfigs([...editedRunConfigs, ...newConfigs]);
                                setEditedDefaultConfigId(editedDefaultConfigId ?? newConfigs[0].id);
                                setHasChanges(true);
                              }
                            }
                          } catch {
                            // silent
                          } finally {
                            setIsDetecting(false);
                          }
                        }}
                        className="btn btn-ghost btn-sm gap-1"
                      >
                        {isDetecting ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Wand2 className="w-3.5 h-3.5" />
                        )}
                        {isDetecting ? 'Detecting...' : 'Auto-Detect'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}

          {section === 'project-queue' && project && (
            <>
              <SectionHeader
                title="Ticket Queue"
                description="Configure which agent plays each role in the autonomous queue. Edits persist to .ateliercode/config.json and take effect on the next scripts/queue/* invocation."
              />
              <QueueSettingsPanel projectId={project.id} />
            </>
          )}

          {section === 'project-channels' && project && (
            <ChannelsSection projectRoot={project.root_path} projectName={project.name} />
          )}
        </div>
      </div>

      {/* ── Sticky save bar ── */}
      {hasChanges && (
        <div className="border-t border-base-300 px-6 py-3 flex items-center justify-between flex-shrink-0 bg-base-100">
          <span className="text-sm text-base-content/40 hidden sm:block">You have unsaved changes</span>
          <div className="flex gap-2 ml-auto">
            <button onClick={handleReset} disabled={isSaving} className="btn btn-ghost btn-sm">Discard</button>
            <button onClick={handleSaveChanges} disabled={isSaving} className="btn btn-primary btn-sm gap-2">
              {isSaving ? <><Loader2 className="w-4 h-4 animate-spin" />Saving...</> : <><Save className="w-4 h-4" />Save</>}
            </button>
          </div>
        </div>
      )}

      <AIProjectDetailsModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onApply={handleApplyAIDetails}
        initialDetails={aiGeneratedDetails}
        isLoading={isRegenerating}
      />
    </>
  );
}

// ─── Layout Position Toggle ──────────────────────────────────────────────────

function LayoutPositionToggle() {
  const sidebarPosition = useUIStore((state) => state.sidebarPosition);
  const setSidebarPosition = useUIStore((state) => state.setSidebarPosition);
  const setBorderPosition = useLayoutStore((state) => state.setBorderPosition);

  const handleChange = (position: 'left' | 'right') => {
    if (position === sidebarPosition) return;
    setSidebarPosition(position);
    setBorderPosition(position);
  };

  return (
    <div className="flex gap-3">
      <button
        onClick={() => handleChange('left')}
        className={`btn flex-1 gap-2 ${sidebarPosition === 'left' ? 'btn-primary' : 'btn-ghost'}`}
      >
        <PanelLeft className="w-4 h-4" />
        Left
      </button>
      <button
        onClick={() => handleChange('right')}
        className={`btn flex-1 gap-2 ${sidebarPosition === 'right' ? 'btn-primary' : 'btn-ghost'}`}
      >
        <PanelRight className="w-4 h-4" />
        Right
      </button>
    </div>
  );
}
